// Add this near the top of App.tsx or create a types/global.d.ts file
declare global {
  interface Window {
    onboardingDebug?: {
      testMessage: (message: string) => void;
    };
    messageLocationMap?: {[messageId: string]: Location[]};
    pendingWeatherWidgets?: {[location: string]: {widgetId: string, messageId: string}};
    // Add these new properties
    rebuildFlagsSet?: boolean;
    isRebuildMode?: boolean;
    latestRebuildMessageId?: string;
    DEBUG: {
      trace: any[];
      log: (msg: string, data?: any) => void;
    };
  }
}


let appMountCounter = 0;

// Add this at the top level of App.tsx - a global handler that can be called from anywhere
function forceUIVisibility() {  
  // 1. Force showUI state
  window.setTimeout(() => {
    try {
      // @ts-ignore - accessing React internals as last resort
      const setShowUIFn = window.__REACT_DEVTOOLS_GLOBAL_HOOK__?.renderers?.get(1)?._rendererInterfaces?.get(1)?._agent?._bridge?.getState()?.showUI?.set;
      if (setShowUIFn) {
        setShowUIFn(true);
        // --- console.log('[App] Forced showUI state via React internals');
      }
    } catch (e) {
      // --- console.log('[App] Could not force state via internals:', e);
    }
  }, 0);

  // 2. Directly show auth container and panels
  setTimeout(() => {
    try {
      // Make auth panel container invisible
      const authContainer = document.getElementById('auth-panel-container');
      if (authContainer) {
        authContainer.style.display = 'none';
        // --- console.log('[App] Hid auth container');
      }
      
      // Force chat panel to be visible
      const chatPanels = document.querySelectorAll('.chat-container, [class*="ChatPanel"]');
      chatPanels.forEach(panel => {
        if (panel instanceof HTMLElement) {
          panel.style.transform = 'translateX(0)';
          panel.style.visibility = 'visible';
          panel.style.opacity = '1';
          panel.style.display = 'block';
          // --- console.log('[App] Forced chat panel visibility:', panel);
        }
      });
      
      // --- console.log('[App] Direct DOM changes applied');
    } catch (e) {
      console.error('[App] Error applying direct DOM changes:', e);
    }
  }, 100);
  
  // 3. Try animation function as well
  /* setTimeout(() => {
    try {
      if (typeof //animatePanels === 'function') {
        // --- console.log('[App] Calling //animatePanels directly');
        animatePanels('slideIn', 'chat');
      }
    } catch (e) {
      console.error('[App] Error calling animatePanels:', e);
    }
  }, 200); */
}

// Add a global window function for debugging
// @ts-ignore
window.forceTripper = forceUIVisibility;

// Add an event dispatch for rebuild complete that always works
function dispatchRebuildComplete(detail: any = {}) {
  // --- console.log('[App] Dispatching rebuild complete event:', detail);
  
  // Dispatch multiple event types to ensure it's caught
  const eventDetail = { ...detail, timestamp: Date.now() };
    
  // Also trigger via direct function call
  setTimeout(() => {
    forceUIVisibility();
  }, 50);
}




// Google Photos API Test
import { initGoogleMaps, isGoogleMapsReady } from './googleMapsInit';
//import GooglePlacesPhotoTester from '../GooglePlacesPhotoTester'; // Adjust the path as needed
//import GoogleMapsPhotoTester from '../GoogleMapsPhotoTester'; // Adjust the path as needed
//import PhotoTesterPortal from '../PhotoTesterPortal'; // Adjust the path as needed
import EnhancedPhotoTester from '../EnhancedPhotoTester'; // Adjust the path as needed

// Import the full data sync handler
import { fullDataSyncHandler } from './services/dataPersistence/fullDataSyncHandler';

import { FEATURES } from '../invite-system/src/config'; // Import directly from invite-system
// Add this console log near the top of your App component
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import * as Sentry from "@sentry/react";

// PDS - Persistent data system
import { DataController } from './components/DataController';
import { DebugOverlay } from './components/DebugOverlay'; 
import { conversationService, initializeDataSystem, mapStateService, itineraryService, dataSyncService } from './services/dataPersistence';
import { processStreamingMessage } from './services/chat/messageProcessor';
import { ExtractionStatus, DataTypeStatus } from './types/chat';


//import SaveIndicator from './components/ui/SaveIndicator'; 

// Import the panel system
import { PanelManager, PanelContainer, PanelToggleButton } from './components/PanelSystem';

// Components
import { ChatPanel } from './components/ChatPanel';
//import { MapPanel } from './components/MapPanel';
import { MapWrapper } from './components/MapWrapper'; // New import

import { MapToggle } from './components/MapToggle';
import { ItineraryPanel } from './components/TripPlanner/ItineraryPanel_minimal';
import { LoadingOverlay } from './components/ui/LoadingOverlay';
import { SentryErrorBoundary } from './sentry';

// Auth components from invite system
import { useAuthContext, AuthPanel } from '../invite-system/src';
import { Navbar } from '../invite-system/src/components/Layout/Navbar';
import { AuthDebugPanel } from '../invite-system/src/components/Dev/AuthDebugPanel';
import { AuthStateChecker } from '../invite-system/src/utils/authStateChecker';
import { authService } from '../invite-system/src/services/AuthService';
import { syncClientWithServer } from './utils/authSync';

// Database Comparison Debug
import DebugComparison from './components/DebugComparison';
import DebugPanel from './components/DebugPanel';


// Services
import { getStreamingChatResponse } from './services/claude';
import { getRandomCity, generateLLMWelcomeMessage, getCityAsLocation, TOP_CITIES } from './services/cityService';
import { processLocationImages, createImageMessage } from './services/imageProcessing';
import { validateQueryAsync } from './services/chat/queryValidator';
import { cityContext } from './services/cityContext';

// Utils
import { resetSeenMarkers } from './utils/markerUtils';

// Types
import { Message, Location } from './types/chat';
import { FlightData } from './types/flights';
import { Itinerary } from './types/itinerary';
import { add } from 'date-fns';


import { googlePhotosService } from './services/GooglePhotosService';

// Conversation restore service
import { fetchAndRebuildFromDatabase } from './services/databaseRebuildService';

import { ConversationRebuildTester } from './components/ConversationRebuildTester';
import DbRebuildContainer from './components/DbRebuildContainer';


// Add to the top of App.tsx
//import { panelAnimator, animatePanels, } from './services/panelAnimationService';
import {TripDetails} from './types/itinerary' 

import './styles/animation.css';
import { globalRegistry } from './globalRegistry';

// Conversation manager
import { ConversationManager } from './services/stateMachine/conversationManager';

// Add to existing imports
import { AbstractWizard } from './services/stateMachine/AbstractWizard';
import { WizardFactory } from './services/stateMachine/wizardFactory';

import { debug } from '../debugLogger';
import { SharePage } from './pages/SharePage';
import { Routes, Route } from 'react-router-dom';


// OnBoarding
//import { UserOnboarding } from './services/onboarding/UserOnboarding';
import { SequentialMessageFlow } from './services/messaging/SequentialMessageFlow';

import { MessageRouter } from './services/chat/MessageRouter';
import { createUserMessage, createBotMessage, generateRandomSuffix, prepareMessagesForClaude } from './services/chat/messageHelpers';
import { processLocationsInBackground } from './services/chat/locationExtractor';
import { DataExtractionRouter } from './services/chat/dataExtractionRouter';

import { useIsMobile } from './hooks/useIsMobile';


// OnBoarding


// Itinerary restore service
import { 
  loadItineraryById 
} from './services/itinerary/builder';


// Initialize Sentry
if (import.meta.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.REACT_APP_SENTRY_DSN,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
  });
}

const SecurityProtections = () => {
  useEffect(() => {
    // Only apply in production
    if (process.env.NODE_ENV !== 'production') {
      // --- console.log('Security protections disabled in development mode');
      return;
    }
    
    // DevTools detection
    let devToolsInterval;
    const checkDevTools = () => {
      const threshold = 160;
      
      devToolsInterval = setInterval(() => {
        const widthDelta = window.outerWidth - window.innerWidth;
        const heightDelta = window.outerHeight - window.innerHeight;
        
        if (widthDelta > threshold || heightDelta > threshold) {
          window.location.reload();
        }
      }, 1000);
    };

    // Initialize selected protections
    checkDevTools();

    // Properly clean up intervals
    return () => {
      if (devToolsInterval) clearInterval(devToolsInterval);
    };
  }, []);

  return null;
};

const SecurityProtections_UNUSED = () => {
  useEffect(() => {
    // Enhanced console protection
    const disableConsole = () => {
      try {
        const consoleMethods = ['log', 'warn', 'error', 'info', 'debug', 'trace'];
        const originalConsole = { ...console };
        
        consoleMethods.forEach(method => {
          console[method] = () => {};
        });

        Object.defineProperty(window, 'console', {
          get: () => ({
            ...originalConsole,
            ...Object.fromEntries(consoleMethods.map(m => [m, () => {}]))
          }),
          set: () => {}
        });
      } catch (e) {
        // Silent fail
      }
    };

    // Input protection
    const disableShortcuts = (e: KeyboardEvent) => {
      const forbiddenKeys = ['F12', 'I', 'J', 'C', 'U'];
      const ctrlShiftCombos = ['Control', 'Shift'];
      
      if (forbiddenKeys.includes(e.key) || 
          (e.ctrlKey && ctrlShiftCombos.some(k => e.getModifierState(k)))) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // UI protection
    const disableUI = () => {
      document.body.addEventListener('contextmenu', e => {
        e.preventDefault();
        return false;
      });

      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    };

    // DevTools detection (enhanced)
    const checkDevTools = () => {
      const devtools = {
        open: false,
        orientation: null
      };
      
      const threshold = 160;
      const emitEvent = (state: boolean) => {
        if (state) {
          window.location.reload(); // Or redirect
        }
      };

      setInterval(() => {
        const widthDelta = window.outerWidth - window.innerWidth;
        const heightDelta = window.outerHeight - window.innerHeight;
        
        if (widthDelta > threshold || heightDelta > threshold) {
          if (!devtools.open) {
            emitEvent(true);
          }
          devtools.open = true;
        } else {
          devtools.open = false;
        }
      }, 500);
    };

    // Initialize protections
    disableConsole();
    //disableUI();
    //window.addEventListener('keydown', disableShortcuts);
    checkDevTools();

    return () => {
      //window.removeEventListener('keydown', disableShortcuts);
    };
  }, []);

  return null;
};

//const REGISTERED_PANELS = React.useRef(false);

export default function App() {

  const isMobile = useIsMobile();


  const mountCount = ++globalRegistry.mountCounter;
  // --- console.log(`[App] MOUNT #${mountCount} at ${new Date().toISOString()}`);


  // ===== INITIALIZE DATA SYSTEM =====
  // State for sync status
  const [syncStatus, setSyncStatus] = useState({
    overall: 'idle',
    lastSyncTime: 0,
    details: {}
  });


  // ===== GOOGLE PHOTOS API =====

  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState(false);
  const [showPhotoTester, setShowPhotoTester] = useState(true);


  useEffect(() => {
    // --- console.log('FEATURES from invite-system:', FEATURES);
  }, []); // Log once on initialization


  // ===== AUTH STATE =====

  // Add this after your state declarations
  const loadingMessages_backup = [
    "Preparing your travel experience...",
    "Finding amazing destinations for you...",
    "Getting your personalized travel companion ready...",
    "Mapping out possibilities...",
    "Almost there, setting up your journey..."
  ];

  const loadingMessages = [
    "Powering up with travel ideas...",
    "Lining up places worth the story...",
    "Prepping your next great escape...",
    "Fueling up with routes and gems...",
    "Getting Trippy ready ready...",
    "Warming up your travel engine...",
    "Tuning the trip to fit you...",
    "Loading up on inspiration..."
  ];

  const { isAuthenticated } = useAuthContext();
  
  const [showUI, setShowUI] = useState(() => {
    const isAuth = isAuthenticated;
    const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
    return isAuth && !isLoggedOut;
  });
  // With these lines:
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true); // Start with overlay visible
  const [loadingMessage, setLoadingMessage] = useState('Preparing your travel experience...');

  const [showRestorationOverlay, setShowRestorationOverlay] = useState(false);


  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const saveData = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      const response = await api.saveConversation(data);
      setSaveSuccess(true);
      // --- console.log('Saved successfully', response);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ===== CHAT & LOCATION STATE =====
  

  const [initialMessage, setInitialMessage] = useState<Message | null>(null);
  const initialCity = useMemo(() => getRandomCity(), []);
  const [currentCityContext, setCurrentCityContext] = useState(cityContext.getCurrentCity());

  //const initialLocation = useMemo(() => getCityAsLocation(initialCity), []);
  
  const initialLocation = useMemo(() => {
    const isAuth = localStorage.getItem('tripperAuthenticated') === 'true';
    const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
    // Only create location object if authenticated
    if (isAuth && !isLoggedOut) {
      return getCityAsLocation(initialCity);
    }
    return null; // Return null if not authenticated
  }, [initialCity]);

  //const initialCity = useMemo(() => getRandomCity(), []);
  //const initialLocation = useMemo(() => getCityAsLocation(initialCity), []);

  // Initialize with empty arrays first
  const [messages, setMessages] = useState<Message[]>([]);
  const [onboardingActive, setOnboardingActive] = useState(false);
  /* const userOnboarding = new UserOnboarding({
    onComplete: (userData) => {
      console.log('Onboarding complete with user data:');
    }
  }); */

  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [hasRestoredConversation, setHasRestoredConversation] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState<Message | null>(null);
  const [mapView, setMapView] = useState<'osm' | 'google'>('osm');
  const [error, setError] = useState<string | null>(null);
  const [currentWeatherLocation, setCurrentWeatherLocation] = useState<string | null>(null);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [locationSelectedFromCard, setLocationSelectedFromCard] = useState(false);

  // ===== ITINERARY STATE =====
  const [currentItinerary, setCurrentItinerary] = useState<Itinerary | null>(null);
  const [showItinerary, setShowItinerary] = useState(false);
  const [streamingActivity, setStreamingActivity] = useState(false);
  const [isStreamingBudget, setIsStreamingBudget] = useState(false); // Add this if missing
  const [isPanelAnimating, setIsPanelAnimating] = useState(false);
  const [showItineraryPanel, setShowItineraryPanel] = useState(true);
  const [refInitialized, setRefInitialized] = useState(false);

  const [preventMarkers, setPreventMarkers] = useState(true);

  // Add these state variables
  const [debugData, setDebugData] = useState(null);
  const [showDebugTable, setShowDebugTable] = useState(false);
  // Add state for debug
  const [showDebug, setShowDebug] = useState(false);

  // In your App component, add state to control the tester visibility
  const [showRebuildTester, setShowRebuildTester] = useState(false);

  const [chatPanelOpacity, setChatPanelOpacity] = useState(0);
  const [isPanelVisible, setIsPanelVisible] = useState(true);

  // Add this state for the conversation manager
  const [conversationManager] = useState<ConversationManager>(() => new ConversationManager());
  const [helpPromptsVisible, setHelpPromptsVisible] = useState(false);





  // Google API Keys
  //const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || 'AIzaSyA4j9qbFHnmqSCZAWzGWFFs2hBhYMvzQtw';

  // ===== FORCE SYNC DATA =====
  // Add this inside your App component
  const [syncDebugResult, setSyncDebugResult] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const [showDebugTool, setShowDebugTool] = useState(false);

  // --- console.log('User data: ----------------- ', userData);

  // Function to collect and display debug data
  const runDebug = () => {
    try {
      // Get user data
      const userData = JSON.parse(localStorage.getItem('tripper_user_data') || '{}');

      // --- console.log('User data: ----------------- ', userData);

      
      // Get token
      const token = localStorage.getItem('authToken');
      
      // Set debug data
      setDebugData({
        userData: userData,
        token: token ? token.substring(0, 20) + '...' : 'None',
        timestamp: new Date().toISOString()
      });
      
      // Show debug panel
      setShowDebug(true);
    } catch (error) {
      console.error("Debug error:", error);
      alert(`Debug error: ${error.message}`);
    }
  };

  // ===== TEST BUTTON =====

  // Create refs for panels
  const chatPanelRef = useRef(null);
  const itineraryPanelRef = useRef<HTMLDivElement | null>(null);
  //const controlsRef = useRef<HTMLDivElement | null>(null);
  //const plannerWizardRef = useRef(null);
  const plannerWizardRef = useRef<{
    submitForm: (details: TripDetails) => boolean;
    populateForm: (details: TripDetails) => boolean;
  }>(null);

  const registeredPanelsRef = useRef(false);

  const togglePanel = () => {
    setIsPanelVisible(prev => !prev);
  };

  const initialChatPosition = 300;
  const initialItineraryPosition = 300;
  const initialControlsPosition = 120;
  // Define initialControlsPosition at module level for consistency

  const [chatPanelWidth, setChatPanelWidth] = useState(350);
  const [itineraryPanelWidth, setItineraryPanelWidth] = useState(380);
  //const itineraryPanelWidth = 500;
  const controlPanelWidth = 120;
  const componentMountingRef = useRef(true);

  const previousMessagesLengthRef = useRef(0);
  const MAX_MESSAGES_TO_LOAD = 50; // Adjust this number as needed



  // Add the function in App.tsx with your other functions
  const testAuth = async () => {
    // Get token from localStorage or wherever you store it
    const token = localStorage.getItem('authToken'); // adjust key name if different
    // --- console.log("Token from storage:", token ? token.substring(0, 15) + "..." : "none");
    
    // Test endpoint
    const response = await fetch('http://localhost:3001/api/auth-debug', {
      headers: { 
        'Authorization': token ? `Bearer ${token}` : '' 
      }
    });
    
    const data = await response.json();
    // --- console.log("Auth debug result:", data);
    return data;
  };

  // Update the useRef type

  // ===== HELPER FUNCTIONS =====

  // Add this helper function inside the App component

const prepareMessagesForClaude = (messages: Message[], currentUserMessage?: string) => {
  // Read from CSS variable
  const CONTEXT_CUTOFF_HOURS = parseFloat(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--chat-context-cutoff-hours') || '24'
  );

  const now = new Date();
  
  // Find the timestamp of the last user message
  const lastMessageTime = messages.length > 0 
  ? new Date(messages[messages.length - 1].timestamp)
  : now;
    
  // Check if there's been a long gap
  const hoursSinceLastMessage = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);
  const hasLongGap = hoursSinceLastMessage > CONTEXT_CUTOFF_HOURS;

  // --- console.log("[prepareMessagesForClaude] Last message time:",hoursSinceLastMessage, hasLongGap);
  
  // Filter messages based on recency
  const recentMessages = messages.filter(msg => {
    const msgTime = new Date(msg.timestamp);
    const hoursAgo = (now.getTime() - msgTime.getTime()) / (1000 * 60 * 60);
    return hoursAgo <= CONTEXT_CUTOFF_HOURS;
  });
  
  // Convert to Claude format
  let claudeMessages = recentMessages.map(msg => ({
    role: msg.sender === 'bot' ? 'assistant' : 'user',
    content: msg.content
  }));
  
  // If there's a gap, add context reset
  if (hasLongGap && messages.length > 0) {
    claudeMessages = [
      {
        role: 'assistant',
        content: 'Welcome back! I notice it\'s been a while since we last chatted. How can I help you today?'
      }
    ];
  }
  
  // Don't add current message here - let caller handle it
  return { claudeMessages, hasLongGap, recentMessages };
};



  // Define outside for better performance
  const notifyDebugOverlay = useCallback(() => {
    // --- console.log('[App] Notifying debug overlay of data update');
    window.dispatchEvent(new CustomEvent('tripper-data-updated'));
  }, []);

  // Fixed findScrollContainer function
  const findScrollContainer = () => {
    const selectors = [
      '.chat-container',
      '.messages-container',
      '.overflow-y-auto',
      '.message-list',
      '[data-testid="chat-messages"]'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        // --- console.log(`[ScrollManager] Found container with selector: ${selector}`);
        return selector; // Return the selector string, not the element
      }
    }
    
    // --- console.log('[ScrollManager] No container found with known selectors');
    return '.overflow-y-auto'; // Default selector as fallback
  };

/*   const DebugTools = () => {
    const resetLocalStorage = () => {
      // Keep only essential data
      const keep = ['ENABLE_AUTH_DEBUG'];
      const toKeep = {};
      
      // Save values we want to keep
      keep.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) toKeep[key] = value;
      });
      
      // Clear all localStorage
      localStorage.clear();
      
      // Restore kept values
      Object.entries(toKeep).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      
      // --- console.log('🧹 LocalStorage cleared except:', Object.keys(toKeep));
      alert('LocalStorage cleared. Reload the page to restart authentication flow.');
    };
    
    return (
      <div style={{ position: 'fixed', bottom: '10px', left: '10px', zIndex: 9999 }}>
        <button 
          onClick={resetLocalStorage}
          style={{ 
            padding: '8px 12px', 
            background: '#ff5555', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Reset localStorage & Test Flow
        </button>
      </div>
    );
  }; */

  // Configure the service
/*   googlePhotosService.configure({
    apiKey: GOOGLE_API_KEY,
    fallbackImage: '/placeholder-image.png'
  }); */
  
  // --- console.log('[App] GooglePhotosService initialized');

  // Add this function to your code and call it where appropriate
  function debugPanels() {
   /*  // --- console.log('==== DEBUG PANEL STATE ====');
    panelAnimator.panels.forEach((panel, id) => {
      // --- console.log(`Panel: ${id}`);
      // --- console.log(`- Element:`, panel.element);
      // --- console.log(`- Width: ${panel.width}`);
      // --- console.log(`- Visible: ${panel.visible}`);
      if (panel.element) {
        const computedStyle = window.getComputedStyle(panel.element);
        // --- console.log(`- Transform: ${computedStyle.transform}`);
        // --- console.log(`- Position: ${computedStyle.position}`);
        // --- console.log(`- Top/Left: ${computedStyle.top}/${computedStyle.left}`);
        // --- console.log(`- Z-Index: ${computedStyle.zIndex}`);
      }
      // --- console.log('------------------------');
    }); */
  }

  /**
   * Load welcome message with internal city generation
   */
   // Get user name if available
  const userName = userData?.name ? userData.name.split(' ')[0] : undefined;
  const generateOnboardingMessage = async () => {

    /* console.log('[ONBOARDING START] Function called');
    console.log('[ONBOARDING START] Current state:', {
      messagesLength: messages.length,
      isStreaming: isStreaming,
      showLoadingOverlay: showLoadingOverlay,
      isAuthenticated: isAuthenticated
    }); */
    // Track messages by index
    const messageRefs: Record<number, string> = {};
    const sequenceId = `onboarding_${Date.now()}`; // Unique ID for this sequence

    // Get user data more reliably
    const userData = JSON.parse(localStorage.getItem('tripper_user_data') || '{}');
    const userEmail = localStorage.getItem('tripperUserEmail');
    
    // Try multiple sources for the name
   /*  const userName = userData?.name || 
                    userData?.displayName || 
                    localStorage.getItem('tripperUserName') ||
                    (userEmail ? userEmail.split('@')[0] : null) || // Use email prefix as fallback
                    'there'; // Final fallback */
    const userName = localStorage.getItem('tripperUserName') || 
                   localStorage.getItem('tripperFullName') ||
                   userData?.name || 
                   userData?.displayName ||
                   'there';           
    console.log('[ONBOARDING] Using userName:', userName);

    const fullName = localStorage.getItem('tripperFullName') || userData?.name || '';
    const firstName = fullName.split(' ')[0] || 'there';
                   
    
    /* const onboardingMessages = [
      `Hey ${userName}! I'm Trippy, your AI travel buddy - I'm so excited to meet you! I can help with anything travel-related and create customized trip plans just for you. What would you like to explore today?`
    ]; */
    
    // Define message contexts
    // `Let the user know they can type "help" anytime for quick ideas`,

     /* const onboardingMessages = [
      `Greet ${firstName} excitedly and say you're Trippy, their travel buddy and it's nice to meet them. Tell them you're here to talk about everything related to travel and also create customised travel plans based on their choices. Ask them how can you help them today?`
    ];  */

    const onboardingMessages = [
    `Hey ${firstName}! I'm Trippy, your travel buddy, so nice to meet you! \n\n I'm here to answer all your questions about travel, show you amazing places, and I can even plan custom trips for you. \n\nHow can I help you today?`];

    
    /* const onboardingMessages = [
      `Express excitement about meeting ${userName || 'the user'}. You're Trippy, their AI travel buddy. You can answer travel questions and create custom trip plans. Ask how you can help today.`
    ]; */

    const onboardingMessages_UNUSED = [
      `Greet ${firstName} excitedly as Trippy, their AI travel buddy and tell them you're happy to meet them`,
      `Tell them you can help them find nice places to visit worldwide - nature spots, attractions, restaurants and you can create customised travel plans based on preferences`,
      `Let the user know they can type "help" anytime for quick ideas`,
      `Ask them What would they like to do? .. Explore destinations, start planning a trip, or just chat about travel`
    ];

    const returningUserMessages = [
      `Welcome back ${firstName}, and ask how they are doing today! Ask them if they have had any recent travel adventures or if they have any upcoming trips planned`,
      `Remember you can type "help" anytime for quick ideas`,
      `Ask them what would they like to explore do?`
    ];

    setShowLoadingOverlay(false);
    
    // Create the flow
    const onboardingFlow = new SequentialMessageFlow(onboardingMessages, {
      messagePause: 500, // 1.5 seconds between messages
      streamingInterval: 40,  // milliseconds between chunks
      streamingSpeed: 3,     // characters per chunk (default is 3)
      onMessageStart: (index) => {
        console.log('[ONBOARDING] Starting message', index);
        // Create empty message with stable ID and sequence metadata
        const messageId = `welcome_${Date.now()}_${index}`;
        const emptyMessage: Message = { 
          id: messageId, 
          content: '', 
          sender: 'bot',
          timestamp: new Date(),
          sequenceId: sequenceId,  // Add sequence tracking
          sequenceIndex: index,     // Position in sequence
          sequenceTotal: onboardingMessages.length  // Total messages in sequence
        };
        // Store reference to update later
        messageRefs[index] = messageId;
        // Add to UI
        setMessages(prev => [...prev, emptyMessage]);
        setCurrentStreamingMessage(emptyMessage);
        setIsStreaming(true);
      },
      onChunkReceived: (index, chunk) => {
        console.log('[SequentialFlow] Chunk received:', {
          index,
          chunk,
          chunkLength: chunk.length,
          chunkCharCodes: chunk.split('').map(c => c.charCodeAt(0)),
          startsWithNewline: chunk.startsWith('\n')
        });
        // Update streaming message in UI
        /* setMessages(prev => 
          prev.map(msg => 
            msg.id === messageRefs[index] 
              ? {...msg, content: msg.content + chunk} 
              : msg
          )
        ); */
        setMessages(prev => 
          prev.map(msg => {
            if (msg.id === messageRefs[index]) {
              const oldContent = msg.content;
              const newContent = oldContent + chunk;
              
              console.log('[SequentialFlow] Message update:', {
                messageId: msg.id,
                oldContent,
                oldLength: oldContent.length,
                newContent,
                newLength: newContent.length
              });
              
              return {...msg, content: newContent};
            }
            return msg;
          })
        );
        // Also update current streaming message
        setCurrentStreamingMessage(prev => {
          if (!prev || prev.id !== messageRefs[index]) return prev;
          return {...prev, content: prev.content + chunk};
        });
      },
      onMessageComplete: (index, message) => {
        const messageId = messageRefs[index];
        
        setMessages(prev => {
          const messageToSave = prev.find(msg => msg.id === messageId);
          
          if (messageToSave) {
            const completeMessage: Message = {
              ...messageToSave,
              content: message,
              sequenceId: sequenceId,
              sequenceIndex: index,
              sequenceTotal: onboardingMessages.length
            };
            
            // Save to persistence
            addMessageToPersistence(completeMessage, true);

            console.log('[ONBOARDING] Message complete: ADDING TO PERSISTENCE ------- ')
            
            // Return updated messages
            return prev.map(msg => msg.id === messageId ? completeMessage : msg);
          }
          
          return prev;
        });
        
        if (index === onboardingMessages.length - 1) {
          setCurrentStreamingMessage(null);
          setIsStreaming(false);
        }
      },
      onSequenceComplete: () => {
        // Mark onboarding as complete
        setOnboardingActive(false);
        
        // Clear streaming state
        setCurrentStreamingMessage(null);
        setIsStreaming(false);
      }
    });
    // Start the flow
    console.log('[ONBOARDING] About to start flow');
    await onboardingFlow.start();
    console.log('[ONBOARDING] Flow started');

  };

  const generateOnboardingMessage_UNUSED = async () => {
    // Check authentication state
    const isAuth = localStorage.getItem('tripperAuthenticated') === 'true';
    const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
    
    // Exit early if not authenticated
    if (!isAuth || isLoggedOut) {
      return;
    }
    
    // Get a random city
    const randomCity = getRandomCity();
    // --- console.log('[App] Loading welcome message for random city:', randomCity.name);
    
    // Get user name if available
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userName = userData?.name ? userData.name.split(' ')[0] : undefined;

    if (localStorage.getItem('authToken')) {
      generateLLMWelcomeMessage(randomCity, userName).then(welcomeMessage => {
        setInitialMessage({
          id: '1',
          content: welcomeMessage,
          sender: 'bot',
          timestamp: new Date()
        });
      });
    } else {
      // Fallback for users without auth token
      setInitialMessage({
        id: '1',
        content: `Hi${userName ? ' ' + userName : ''}! I'm Trippy, your AI travel companion. I've pulled up ${randomCity.name}, ${randomCity.country} for us to explore. ${randomCity.description} What would you like to discover today?`,
        sender: 'bot',
        timestamp: new Date()
      });
    }
  };


  const welcomeBackMessages = [
    `Welcome back${userName ? ' ' + userName : ''}! It's been a while since we last chatted. What would you like to explore today?`,
    `Hey there${userName ? ' ' + userName : ''}! Good to see you again. Where shall we adventure to today?`,
    `Welcome back${userName ? ' ' + userName : ''}! Ready to discover some new places?`,
    `It's been a while${userName ? ' ' + userName : ''}! What's on your travel mind today?`,
    `Great to have you back${userName ? ' ' + userName : ''}! Looking to plan a new trip or explore somewhere interesting?`,
    `Hello again${userName ? ' ' + userName : ''}! I've missed our travel chats. What destination are you curious about?`,
    `Welcome back${userName ? ' ' + userName : ''}, traveler! Where does your wanderlust point today?`,
    `Hey${userName ? ' ' + userName : ''}! It's been some time. Ready to explore the world together again?`,
    `Good to see you again${userName ? ' ' + userName : ''}! Any new travel dreams brewing?`,
    `Welcome back${userName ? ' ' + userName : ''}! Still got that travel bug? Let's find your next adventure!`
  ];

  

  // Near the start of your App component
  useEffect(() => {
     const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
      if (isLoggedOut) {
        setShowUI(false);
        return;
      }
    // THIS NEEDS TO BE CORRECTED - IT'S A TEMP FIX
    // Check for auth token/authenticated state mismatch
    //const isAuthenticated = localStorage.getItem('tripperAuthenticated') === 'true';
    //const hasAuthToken = !!localStorage.getItem('authToken');
  }, []);

  // Add this to the top of your App component
// Then in your App component, replace the current panel registration useEffect

  // Near the top of your App component
  useEffect(() => {
    // Log all localStorage keys for debugging
    console.log('🔑 CURRENT LOCALSTORAGE KEYS:', 
      Object.keys(localStorage).join(', '));
    
    // Set critical default values if missing
    const defaults = {
      'tripperLoggedOut': 'false',
      'server_data_loaded': 'false',
      'conversation_restored': 'false'
    };
    
    Object.entries(defaults).forEach(([key, value]) => {
      if (localStorage.getItem(key) === null) {
        console.log(`⚠️ Setting missing localStorage key: ${key} = ${value}`);
        localStorage.setItem(key, value);
      }
    });
    
    // Debug login state
    console.log('📊 AUTH STATE:', {
      authenticated: localStorage.getItem('tripperAuthenticated'),
      loggedOut: localStorage.getItem('tripperLoggedOut'),
      hasToken: !!localStorage.getItem('authToken'),
      userId: localStorage.getItem('tripperUserId'),
      email: localStorage.getItem('tripperUserEmail')
    });
  }, []);

  useEffect(() => {
    // Check localStorage directly
    try {
      const userData = JSON.parse(localStorage.getItem('tripper_user_data') || '{}');
    } catch (e) {
      console.error('Error checking local storage:', e);
    }
    
    // Also log messages state
    // --- console.log('Messages state:', messages.length);
  }, [messages]);
  
  // Add this helper function to your App component with proper TypeScript types
  useEffect(() => {
    if (!registeredPanelsRef.current && chatPanelRef.current && itineraryPanelRef.current) {
      registeredPanelsRef.current = true;
      // && controlsRef.current
      // Register panels with fixed widths
      //panelAnimator.registerPanel('chat', chatPanelRef.current, chatPanelWidth);
      //panelAnimator.registerPanel('itinerary', itineraryPanelRef.current, itineraryPanelWidth); 
      //panelAnimator.registerPanel('mapControls', controlsRef.current, controlPanelWidth); // Use 120 not 520
      
      // Hide chat initially
      //setTimeout(() => animatePanels('slideOut', 'chat'), 50);
    }
    
    return () => {
      // Only unregister on component unmount
      if (registeredPanelsRef.current && !componentMountingRef.current) {
       //panelAnimator.unregisterPanel('chat');
        //panelAnimator.unregisterPanel('itinerary');
        //panelAnimator.unregisterPanel('mapControls');
        registeredPanelsRef.current = false;
      }
    };
  }, []); // Empty dependency array - only runs once

// Add this effect to track mounting state
  useEffect(() => {
    componentMountingRef.current = false;
  }, []);

  const triggerAnimation = useCallback((
    action: 'slideIn' | 'slideOut' | 'swapInOut' | 'swapOutIn' | 'slideInWithRef' | 'slideOutWithRef',
    target: string | string[],
    padding?: number
  ): boolean => {
    // Check if panels are registered
    if (!registeredPanelsRef.current) {
      console.warn('[App] Cannot animate - panels not registered yet, retrying in 100ms...');
      
      // Retry once after a short delay
      setTimeout(() => {
        // --- console.log('[App] Retrying animation');
        //animatePanels(action, target, padding);
      }, 100);
      
      return false;
    }
    
    // --- console.log(`[App] Triggering ${action} for`, target);
    //animatePanels(action, target, padding);
    return true;
  }, []);

  // 2. Modify the authentication flow in App.tsx
  // Replace the previous auth effect with this event-based approach
  useEffect(() => {
    // --- console.log('[App] Checking authentication state:', isAuthenticated);
    
    if (isAuthenticated) {
      // --- console.log('[APP LOGS][1] Authentication detected, preparing data load');
      setShowLoadingOverlay(true);
      setLoadingMessage('Preparing your experience...');
      
      // Initialize data system
      const userId = localStorage.getItem('tripperUserId') || 'anonymous';
      initializeDataSystem(userId);
      
      // ALWAYS load data from server first
      fullDataSyncHandler.initializeFromServer()
        .then(success => {
          // --- console.log(`[APP LOGS][3] Server data initialization ${success ? 'completed' : 'failed'}`);
          
          // Wait for panel registration to complete
          setTimeout(() => {
            console.log('[APP LOGS] About to call restoreConversationFromDatabase');
            // Start the rebuild process but DON'T show UI yet
            restoreConversationFromDatabase()
              .then(hasExistingConversation => {

                console.log('[APP LOGS] restoreConversationFromDatabase returned:', hasExistingConversation);

                // Replace this part of the restoreConversationFromDatabase function:
                if (!hasExistingConversation) {
                  // --- console.log('[APP LOGS][6] First-time user detected, generating welcome message');
                  
                  console.log('[APP LOGS] No existing conversation found');

                  // Check if user needs onboarding
                  const userId = localStorage.getItem('tripperUserId') || 'anonymous';

                  console.log('[APP LOGS] UserId for onboarding check:', userId);

                  const hasCompletedOnboarding = localStorage.getItem(`user_${userId}_onboarded`) === 'true';
                  console.log('[APP LOGS] Has completed onboarding?', hasCompletedOnboarding);
  
                  
                  if (!hasCompletedOnboarding) {
                    console.log('[APP LOGS] Should trigger onboarding now!');
                    // --- console.log('[APP LOGS] New user detected, will trigger onboarding after rebuild');
                    generateOnboardingMessage();
                    // Just mark conversation as restored but don't set any messages
                    // The onboarding effect will handle setting the welcome message
                    
                    // Set a flag to indicate onboarding should be triggered
                    localStorage.setItem(`user_${userId}_needs_onboarding`, 'true');
                    
                    // We won't set any messages here - onboarding will do that later
                  } else {
                    // For users who completed onboarding, show standard welcome
                    // --- console.log('[APP LOGS] Returning user detected, showing welcome message');
                  }
                  
                  // Dispatch rebuild complete event
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('tripper-rebuild-complete', {
                      detail: { success: true, messageCount: 1, firstTime: true }
                    }));
                  }, 500);
                  
                  return false;
                }
                
                setHasRestoredConversation(true);
                setShowLoadingOverlay(false);
              })
              .catch(error => {
                console.error('[APP LOGS] Error in promise chain:', error);
              });
          }, 300);
        })
        .catch(error => {
          console.error('[APP LOGS][ERROR] Error loading data from server:', error);
          setShowLoadingOverlay(false);
          
          // Dispatch error event
          window.dispatchEvent(new CustomEvent('tripper-rebuild-complete', {
            detail: { success: false, error: error.message }
          }));
        });
    } else {
      // Not authenticated, hide overlay
      setShowLoadingOverlay(false);
    }
  }, [isAuthenticated]);

  // 3. Add an event listener for the rebuild complete event
  useEffect(() => {
    const handleRebuildComplete = (event: CustomEvent) => {
      const detail = event.detail;
      // --- console.log('[App] Rebuild complete event received:', detail);
      
      // Only now display the UI and panels
      setShowUI(true);
      
      // Make sure chat panel is visible
      if (registeredPanelsRef.current) {
        // --- console.log('[App] Showing chat panel after rebuild complete');
        //animatePanels('slideIn', 'chat');
        
        // In the handleRebuildComplete useEffect
        setTimeout(() => {
          // --- console.log('[App] Hiding loading overlay after panel animation');
          setShowLoadingOverlay(false); // Hide overlay first
          
          // Then check for gap after a small delay
          setTimeout(() => {
            // --- console.log('[App] Checking for long gap after rebuild complete ',messages.length);
            //if (messages.length > 0) {
            const { hasLongGap } = prepareMessagesForClaude(messages);
            // --- console.log('[App] Long gap check result:', hasLongGap);
            if (hasLongGap) {
              cityContext.setCurrentCity('');
              
              const randomMessage = welcomeBackMessages[Math.floor(Math.random() * welcomeBackMessages.length)];
              // --- console.log('[App] Adding welcome back message due to long gap:', randomMessage);

              const welcomeBackMessage: Message = {
                id: Date.now().toString(),
                content: randomMessage,
                sender: 'bot',
                timestamp: new Date()
              };
              
              setMessages(prev => [...prev, welcomeBackMessage]);
              addMessageToPersistence(welcomeBackMessage, true);
            }
            //}
          }, 200); // Small delay after overlay hidden
        }, 600); // After panel animation
      } else {
        // --- console.log('[App] Panels not registered yet, will retry');
        setTimeout(() => {
          //animatePanels('slideIn', 'chat');
          
          setTimeout(() => {
            setShowLoadingOverlay(false);
            
            // Add gap check here too
            setTimeout(() => {
              if (messages.length > 0) {
                const { hasLongGap } = prepareMessagesForClaude(messages);
                
                if (hasLongGap) {
                  cityContext.setCurrentCity('');
                  
                  const randomMessage = welcomeBackMessages[Math.floor(Math.random() * welcomeBackMessages.length)];
                  const welcomeBackMessage: Message = {
                    id: Date.now().toString(),
                    content: randomMessage,
                    sender: 'bot',
                    timestamp: new Date()
                  };
                  setMessages(prev => [...prev, welcomeBackMessage]);
                  addMessageToPersistence(welcomeBackMessage, true);
                }
              }
            }, 200);
          }, 300);
        }, 300);
      }      
      // Set UI visibility
      setShowUI(true);

      /* // If this was first-time user, also show map controls
      if (detail?.firstTime) {
        setTimeout(() => {
          // --- console.log('[App] First time user, showing map controls');
          //animatePanels('slideIn', 'mapControls');
        }, 500);
      } */
    };
    
    // Listen for our custom rebuild complete event
    window.addEventListener('tripper-rebuild-complete', handleRebuildComplete as EventListener);
    
    return () => {
      window.removeEventListener('tripper-rebuild-complete', handleRebuildComplete as EventListener);
    };
  }, [messages]); // Ensure fresh messages


  // Update the restoreConversationFromDatabase function in App.tsx

  const restoreConversationFromDatabase = async () => {
    try {
      // Show loading overlay first, then chat panel
      setShowRestorationOverlay(true);
      //animatePanels('slideIn', 'chat');
      
      // Dispatch event that chat panel is visible but loading
      window.dispatchEvent(new CustomEvent('tripper-chat-loading', {
        detail: { visible: true }
      }));
      
      // --- console.log('[APP LOGS][4] Attempting to restore conversation from database');
      
      // Load server data if needed
      const serverDataLoaded = localStorage.getItem('server_data_loaded') === 'true';
      if (!serverDataLoaded) {
        // --- console.log('[APP LOGS] Server data not loaded yet, loading now...');
        await fullDataSyncHandler.initializeFromServer();
      }
      
      // Start the database rebuild
      const result = await fetchAndRebuildFromDatabase();
      console.log('[APP LOGS][5] Database rebuild result:', {
        hasMessages: !!result?.messages?.length,
        messageCount: result?.messages?.length || 0,
        locationCount: result?.locations?.length || 0
      });

      // Inside restoreConversationFromDatabase function:
      if (result && result.messages && result.messages.length > 0) {
        // --- console.log('[APP LOGS][8] Found existing conversation with', result.messages.length, 'messages');
        
        // Set window.messageLocationMap - KEEP THE FULL MAP FOR REFERENCE
        window.messageLocationMap = result.messageLocationMap || {};

        interface ProcessedMessage extends Message {
          _associatedLocations?: Location[];
          _key?: string;
        }
        
        // Process existing messages
        const messagesWithLocations = result.messages.map(msg => ({
          ...msg,
          _associatedLocations: (result.messageLocationMap && result.messageLocationMap[msg.id]) || [],
          _key: `${msg.id}_${Date.now()}`
        })) as ProcessedMessage[];
        
        // ONLY take the most recent X messages
        const recentMessages = messagesWithLocations.length > MAX_MESSAGES_TO_LOAD 
          ? messagesWithLocations.slice(-MAX_MESSAGES_TO_LOAD) 
          : messagesWithLocations;
        
        // --- console.log(`[APP LOGS] Loading ${recentMessages.length} most recent messages out of ${messagesWithLocations.length} total`);
        


        // IMPORTANT: ONLY show locations for the last message with associated locations
        let lastMessageWithLocations: ProcessedMessage | null = null;
        
        // Find the last message that has associated locations
        for (let i = recentMessages.length - 1; i >= 0; i--) {
          const msg = recentMessages[i];
          const messageId = msg.id;
          const associatedLocations = window.messageLocationMap[messageId] || [];
          
          if (associatedLocations.length > 0) {
            lastMessageWithLocations = msg;
            break;
          }
        }
        
        // Update messages state
        setMessages(recentMessages);
        
        // Only set locations from the most recent message that has locations
        if (lastMessageWithLocations) {
          const lastMessageId = lastMessageWithLocations.id;
          const lastMessageLocations = window.messageLocationMap[lastMessageId] || [];
          
          // --- console.log(`[APP LOGS] Showing ONLY ${lastMessageLocations.length} locations from the last relevant message`);
          
          // Set only these locations as visible
          setLocations(lastMessageLocations);
          
          // Set selected location if available
          if (lastMessageLocations.length === 1) {
            setSelectedLocation(lastMessageLocations[0]);
          } else {
            setSelectedLocation(null);
          }
          
          // Update map state service to ONLY show these locations
          mapStateService.updateVisibleLocations(lastMessageLocations);
          
          // Create an explicit string array of IDs
          const visibleLocationIds: string[] = lastMessageLocations.map(loc => String(loc.id));
          
          // Update map state with string array
          mapStateService.updateMapState({
            visibleLocationIds: visibleLocationIds
          });
        }
        else {
          // No messages with locations, clear the map
          // --- console.log('[APP LOGS] No messages with locations found, clearing map');
          setLocations([]);
          setSelectedLocation(null);
          mapStateService.updateVisibleLocations([]);
          mapStateService.updateMapState({
            visibleLocationIds: []
          });
        }
        // Weather location handling remains the same
        if (result.weatherLocation) {
          // --- console.log('[APP LOGS] Setting weather location:', result.weatherLocation);
          setCurrentWeatherLocation(result.weatherLocation);
        }
        // IMPROVED: Scroll handling after rebuild
        // First, ensure scrolling is unblocked
        // Extract city context from recent messages

        if (result && result.messages && result.messages.length > 0) {
          // Take last 10 messages (or all if fewer than 10)
          const recentMessages = result.messages.slice(-10);
          let detectedCity = '';
          
          // Process in reverse chronological order (newest first)
          for (let i = recentMessages.length - 1; i >= 0; i--) {
            const message = recentMessages[i];
            
            // Skip user messages (only look at bot responses)
            if (message.sender !== 'bot') continue;
            
            try {
              // Try to extract location from message content
              // 1. Check for weather responses
              const weatherMatch = message.content.match(/weather in ([^.!?,]+)/i);
              if (weatherMatch && weatherMatch[1]) {
                detectedCity = weatherMatch[1].trim();
                break;
              }
              
              // 2. Check for travel-related responses with locations
              const travelMatch = message.content.match(/(?:in|at|to|about|visit|explore) ([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)/);
              if (travelMatch && travelMatch[1]) {
                detectedCity = travelMatch[1].trim();
                break;
              }
              
              // 3. Try to extract location from JSON content if present
              const jsonMatch = message.content.match(/\{[\s\S]*?\}/);
              if (jsonMatch) {
                try {
                  const data = JSON.parse(jsonMatch[0]);
                  if (data.locations && data.locations[0] && data.locations[0].city) {
                    detectedCity = data.locations[0].city;
                    break;
                  }
                } catch (e) {
                  // JSON parsing failed, continue
                }
              }
            } catch (e) {
              console.error('[App] Error extracting city from message:', e);
            }
          }
          
          // Set city context if found
          if (detectedCity) {
            // --- console.log(`[App] Setting city context from rebuild: ${detectedCity}`);
            cityContext.setCurrentCity(detectedCity);
          }
        }
        
        setTimeout(() => {
          setShowRestorationOverlay(false);
          
          // Keep main loading overlay visible until rebuild complete handlers run
          // --- console.log('[APP LOGS] Dispatching rebuild complete event');
          window.dispatchEvent(new CustomEvent('tripper-rebuild-complete', {
            detail: { 
              success: true, 
              messageCount: result.messages.length,
              timestamp: Date.now()
            }
          }));
          // Don't set showLoadingOverlay to false here
        }, 300);
        // Add one final scroll attempt after rebuild event has been processed
        setTimeout(() => {
          const chatContainer = document.querySelector('.chat-container') || 
                            document.querySelector('.messages-container') ||
                            document.querySelector('.overflow-y-auto');
          
          if (chatContainer && chatContainer instanceof HTMLElement) {
            // --- console.log('[APP LOGS] Final direct scroll attempt');
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 1500);
        
        // Mark that conversation has been restored
        localStorage.setItem('conversation_restored', 'true');
        return true;
      } else {
        // --- console.log('[APP LOGS][5] No existing conversation found in database');

        console.log('[APP LOGS] No existing conversation found in database');

        // ADD YOUR ONBOARDING LOGIC HERE
        const userId = localStorage.getItem('tripperUserId') || 'anonymous';
        console.log('[APP LOGS] Checking onboarding for userId:', userId);
        
        const hasCompletedOnboarding = localStorage.getItem(`user_${userId}_onboarded`) === 'true';
        if (!hasCompletedOnboarding) {
          console.log('[APP LOGS] Triggering onboarding');
          generateOnboardingMessage();
          localStorage.setItem(`user_${userId}_needs_onboarding`, 'true');
        }
        
        // Hide overlay for empty results
        setShowRestorationOverlay(false);
        
        // Dispatch event even with no messages
        window.dispatchEvent(new CustomEvent('tripper-rebuild-complete', {
          detail: { 
            success: true, 
            messageCount: 0, 
            empty: true,
            timestamp: Date.now()
          }
        }));
        
        return false;
      }
    } catch (error) {
      console.error('[APP] Error rebuilding from database:', error);
      
      // Hide overlay on error
      setShowRestorationOverlay(false);
      
      // Still show panel on error and dispatch event with error status
      //animatePanels('slideIn', 'chat');
      
      window.dispatchEvent(new CustomEvent('tripper-rebuild-complete', {
        detail: { 
          success: false, 
          error: error instanceof Error ? error.message : String(error),
          timestamp: Date.now()
        }
      }));
      
      return false;
    }
  };

  // Add this debug helper to the App.tsx file, near the top with other utilities

  // Scroll debugger helper function
  const debugScroll = (enabled = false) => {
    if (!enabled) return;
    
    const debugIndicator = document.createElement('div');
    debugIndicator.style.position = 'fixed';
    debugIndicator.style.bottom = '10px';
    debugIndicator.style.right = '10px';
    debugIndicator.style.zIndex = '10000';
    debugIndicator.style.background = 'rgba(0,0,0,0.7)';
    debugIndicator.style.color = 'white';
    debugIndicator.style.padding = '8px 12px';
    debugIndicator.style.borderRadius = '4px';
    debugIndicator.style.fontSize = '12px';
    debugIndicator.style.fontFamily = 'monospace';
    debugIndicator.textContent = 'Scroll: Initializing...';
    document.body.appendChild(debugIndicator);
    
    // Find all potential scroll containers
    const containers = [
      '.chat-container',
      '.messages-container',
      '.overflow-y-auto',
      '.message-list'
    ].map(selector => document.querySelector(selector))
    .filter(Boolean) as HTMLElement[];
    
    if (containers.length === 0) {
      debugIndicator.textContent = 'Scroll: No containers found';
      return;
    }
    
    // Add scroll position indicators to each container
    containers.forEach((container, index) => {
      // Add visual indicator
      const indicator = document.createElement('div');
      indicator.style.position = 'absolute';
      indicator.style.right = '0';
      indicator.style.top = '0';
      indicator.style.width = '10px';
      indicator.style.height = '100%';
      indicator.style.background = 'linear-gradient(to bottom, red, yellow, green)';
      indicator.style.opacity = '0.3';
      indicator.style.pointerEvents = 'none';
      indicator.style.zIndex = '9999';
      container.style.position = 'relative';
      container.appendChild(indicator);
      
      // Add scroll marker
      const marker = document.createElement('div');
      marker.style.position = 'absolute';
      marker.style.right = '0';
      marker.style.width = '10px';
      marker.style.height = '3px';
      marker.style.background = 'white';
      marker.style.zIndex = '10000';
      marker.style.pointerEvents = 'none';
      container.appendChild(marker);
      
      // Monitor scroll position
      const updateScrollInfo = () => {
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const maxScroll = scrollHeight - clientHeight;
        const scrollPercentage = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 100;
        
        // Update marker position
        marker.style.top = `${(scrollTop / scrollHeight) * 100}%`;
        
        // Update debug info
        debugIndicator.innerHTML = `
          Scroll #${index}: ${scrollPercentage.toFixed(1)}%<br>
          Position: ${scrollTop}px / ${maxScroll}px<br>
          Container: ${scrollHeight}px × ${clientHeight}px
        `;
      };
      
      // Update on scroll
      container.addEventListener('scroll', updateScrollInfo);
      
      // Update every 500ms anyway
      setInterval(updateScrollInfo, 500);
      
      // Initial update
      updateScrollInfo();
      
      // Add manual scroll controls
      const controls = document.createElement('div');
      controls.style.position = 'fixed';
      controls.style.bottom = '100px';
      controls.style.right = '10px';
      controls.style.zIndex = '10000';
      controls.style.display = 'flex';
      controls.style.flexDirection = 'column';
      controls.style.gap = '5px';
      controls.innerHTML = `
        <button id="scroll-top" style="padding:5px;background:#333;color:white;border:none;border-radius:3px">Top</button>
        <button id="scroll-bottom" style="padding:5px;background:#333;color:white;border:none;border-radius:3px">Bottom</button>
        <button id="scroll-mid" style="padding:5px;background:#333;color:white;border:none;border-radius:3px">Middle</button>
      `;
      document.body.appendChild(controls);
      
      document.getElementById('scroll-top')?.addEventListener('click', () => {
        container.scrollTop = 0;
      });
      
      document.getElementById('scroll-bottom')?.addEventListener('click', () => {
        container.scrollTop = container.scrollHeight;
      });
      
      document.getElementById('scroll-mid')?.addEventListener('click', () => {
        container.scrollTop = container.scrollHeight / 2;
      });
    });
    
    // --- console.log('[Scroll Debug] Initialized for', containers.length, 'containers');
  };


  // 5. Add a manual test function to App.tsx
  const testImageSync = () => {
    // --- console.log('=== MANUAL IMAGE SYNC TEST ===');
    
    // Check if sync service exists
    const syncService = fullDataSyncHandler;
    // --- console.log('Sync service exists:', !!syncService);
    
    // Check current sync state
    // --- console.log('Sync state:', syncService.getSyncStatus());
    
    // Check for changed entities
    const changedImages = dataSyncService.getChangedEntityIds('images');
    // --- console.log('Changed image IDs:', Array.from(changedImages));
    
    // Check images in storage
    const userData = JSON.parse(localStorage.getItem('tripper_user_data') || '{}');
    // --- console.log('Images in storage:', userData.images?.messages?.length || 0);
    
    // Force a sync
    // --- console.log('Forcing sync...');
    fullDataSyncHandler.syncAllData();
  };

  // Update the createScrollManager function in App.tsx

  // Coordinate onboarding with conversation rebuilding
  
  // Add this function to the App component
  const addMessageToPersistence = useCallback((message: Message, parseContent = false) => {
    // --- console.log(`[ APP - ConversationService] Adding ${message.sender} message:`, message.id);
    
    // Improve the duplicate detection by checking the actual message content
    const existingSequence = conversationService.getConversationSequence();

    // --- console.log(`[ APP - ConversationService] Existing sequence: ----------- `, existingSequence);
    const isDuplicate = existingSequence.some(element => {
      //console.log(`[ APP - ConversationService] Checking element:`, element);
      if (element.type === 'message') {
        try {
          const parsedMessage = JSON.parse(element.elementId);
          // More thorough check to catch weather messages
          return parsedMessage.id === message.id || 
                 (parsedMessage.content === message.content && 
                  parsedMessage.sender === message.sender &&
                  Math.abs(new Date(parsedMessage.timestamp).getTime() - 
                           new Date(message.timestamp).getTime()) < 1000);
        } catch (e) {
          console.error('[ APP - ConversationService] Error parsing existing message element:', e);
          return false;
        }
      }
      console.log(`[ APP - ConversationService] Element is not a message, skipping`);
      return false;
    });

    /* if (isDuplicate) {
      // THIS BASTARD IS A DUPLICATE!
      // --- console.log(`[ APP - ConversationService] Message ${message.id} already exists or is duplicate, skipping`);
      console.warn(`[ APP - ConversationService] Duplicate message detected: ${message.id}`);
      return;
    } */
    if (isDuplicate) {
      // Allow update if adding _extractedData to existing message
      if (parseContent && message._extractedData) {
        // Process locations from _extractedData without re-adding message
        // Just process the location cards here
      } else {
        console.warn(`[ APP - ConversationService] Duplicate message detected: ${message.id}`);
        return;
      }
    }
    // In addMessageToPersistence function in App.tsx
    
    if (parseContent && message.sender === 'bot') {
      console.log('[ APP - ConversationService] Parsing bot message content:', message.content);
      try {
        // Parse message content to separate text from JSON data
        const { textContent, jsonContent, weatherLocation } = processStreamingMessage(message.content);
        
        // First add the basic message with ONLY the text content (no JSON)
        const cleanContent = textContent || message.content.replace(/\{[\s\S]*\}/g, '').trim();
        
        conversationService.addMessage({
          id: message.id,
          content: cleanContent,
          sender: message.sender,
          timestamp: message.timestamp.toISOString()
        });
        //notifyDebugOverlay();

        // Process weather data if present
        if (weatherLocation) {
          // --- console.log(`[ APP - ConversationService] Detected weather data for ${weatherLocation}`);
          
          // Create a weather widget placeholder - we'll update it when data is loaded
          const weatherWidgetId = `weather_${Date.now()}_${Math.random().toString(36).slice(2)}`;

          // Add a property to track the message that triggered this weather widget
          // This allows us to update the widget later when data is received
          window.pendingWeatherWidgets = window.pendingWeatherWidgets || {};
          window.pendingWeatherWidgets[weatherLocation] = {
            widgetId: weatherWidgetId,
            messageId: message.id
          };

          // Create a weather widget placeholder with space for forecast
          const weatherWidget = {
            id: weatherWidgetId,
            location: weatherLocation,
            timestamp: message.timestamp.toISOString(),
            // Add empty placeholders for data that will be filled later
            temperature: null,
            condition: null,
            forecast: []
          };
          
          // Add the weather widget to conversation
          conversationService.addWeatherWidget(weatherWidget, message.id);
          //notifyDebugOverlay();
          return;
        }
        
        // Process location data if present
        console.log('[ APP - ConversationService] Processing extracted location data from message: ----- ', message._extractedData);
        if (jsonContent || message._extractedData?.locations || message._extractedData?.restaurant) {
          try {
            let locationsToProcess;
            
            // Prioritize _extractedData over jsonContent
            /* if (message._extractedData?.locations) {
              locationsToProcess = message._extractedData.locations;
              console.log('[ APP - ConversationService] Using _extractedData locations:', locationsToProcess);
            } else if (jsonContent) {
              const data = JSON.parse(jsonContent);
              locationsToProcess = data.locations;
            } */
            // Prioritize _extractedData over jsonContent
            if (message._extractedData?.locations) {
              locationsToProcess = message._extractedData.locations;
              console.log('[ APP - ConversationService] Using _extractedData locations:', locationsToProcess);
            } else if (message._extractedData?.restaurant) {
              locationsToProcess = message._extractedData.restaurant;
              console.log('[ APP - ConversationService] Using _extractedData restaurants:', locationsToProcess);
            } else if (jsonContent) {
              const data = JSON.parse(jsonContent);
              locationsToProcess = data.locations;
            }
            console.log('[ APP - ConversationService] Using _extractedData locations:', locationsToProcess, Array.isArray(locationsToProcess));
            if (locationsToProcess && Array.isArray(locationsToProcess)) {
              // Process each location with unified logic
              locationsToProcess.forEach((loc: any, index: number) => {
                if (loc && loc.position && loc.position.lat && loc.position.lng) {
                  const messageTimestamp = message.id.match(/\d{13}/)?.[0] || Date.now().toString();

                  /* const locationCard = {
                    //id: `loc_${Date.now()}_${index}`,
                    id: `loc_${messageTimestamp}_${index}`, // Use SAME timestamp
                    name: loc.name,
                    city: loc.city || '',
                    country: loc.country || '',
                    type: loc.type || 'place', // <-- Add this with fallback
                    position: {
                      lat: Number(loc.position.lat),
                      lng: Number(loc.position.lng)
                    },
                    rating: loc.rating || 4.5,
                    reviews: loc.reviews || 1000,
                    description: loc.description || '',
                    timestamp: message.timestamp.toISOString()
                  }; */
                  
                  /* const locationCard_UNUSED = {
                    id: `loc_${messageTimestamp}_${index}`,
                    name: loc.name,
                    city: loc.city || '',
                    country: loc.country || '',
                    //type: loc.type || (message._extractedData?.type === 'restaurant' ? 'restaurant' : 'place'),
                    type: loc.type || (message._extractedData?.restaurant ? 'restaurant' : 'place'),
                    position: {
                      lat: Number(loc.position.lat),
                      lng: Number(loc.position.lng)
                    },
                    rating: loc.rating || 4.5,
                    reviews: loc.reviews || 1000,
                    description: loc.description || '',
                    timestamp: message.timestamp.toISOString(),
                    // Add restaurant-specific fields
                    cuisine: loc.cuisine,
                    priceLevel: loc.priceLevel,
                    formattedAddress: loc.formattedAddress,
                    businessStatus: loc.businessStatus,
                    placeId: loc.placeId
                  }; */

                  const locationCard = {
                    id: `loc_${messageTimestamp}_${index}`,
                    name: loc.name,
                    city: loc.city || '',
                    country: loc.country || '',
                    type: loc.type || 'place',
                    position: {
                      lat: Number(loc.position.lat),
                      lng: Number(loc.position.lng)
                    },
                    rating: loc.rating || 4.5,
                    reviews: loc.reviews || 1000,
                    description: loc.description || '',
                    timestamp: message.timestamp.toISOString(),
                    // Add ALL fields from enriched data:
                    cuisine: loc.cuisine,
                    priceLevel: loc.priceLevel,
                    formattedAddress: loc.formattedAddress,
                    businessStatus: loc.businessStatus,
                    placeId: loc.placeId,
                    types: loc.types[0], // Add this
                    googleName: loc.googleName // Add this if needed
                  };

                  // Add logging here:
                  console.log('[APP CREATE] Location from extraction:', {
                      original: loc,
                      position: loc.position,
                      lat: loc.position?.lat,
                      lng: loc.position?.lng,
                      converted: {
                          lat: Number(loc.position?.lat),
                          lng: Number(loc.position?.lng)
                      }
                  });

                  //console.log("City -- ",loc.city);
                  conversationService.addLocationCard(locationCard, message.id);
                }
              });
            }
          } catch (error) {
            console.error('[ APP - ConversationService] Error parsing location data:', error);
          }
        }
        // Notify debug overlay of message addition
        //notifyDebugOverlay();
      } catch (error) {
        console.error('[ APP - ConversationService] Error processing message content:', error);
        
        // Fallback: add the message as-is if parsing fails
        conversationService.addMessage({
          id: message.id,
          content: message.content,
          sender: message.sender,
          timestamp: message.timestamp.toISOString(),
          ...(message._extractedData && { _extractedData: message._extractedData })
        });
        
        // Notify debug overlay
        //notifyDebugOverlay();
      }
    } else {
      console.log('[ APP - ConversationService] Adding message without parsing:', message.content);
      // Simple case: just add the message directly
      conversationService.addMessage({
        id: message.id,
        content: message.content,
        sender: message.sender,
        timestamp: message.timestamp.toISOString(),
        ...(message._extractedData && { _extractedData: message._extractedData })
      });
      
      // Notify debug overlay
      //notifyDebugOverlay();
    }
  }, [notifyDebugOverlay]);

  // Add this test function to App.tsx

  // Add to App.tsx
  const testWelcomeMessage = () => {
    // Create a modal/popup to display results
    const testContainer = document.createElement('div');
    testContainer.style.position = 'fixed';
    testContainer.style.top = '50%';
    testContainer.style.left = '50%';
    testContainer.style.transform = 'translate(-50%, -50%)';
    testContainer.style.padding = '20px';
    testContainer.style.background = 'white';
    testContainer.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
    testContainer.style.borderRadius = '8px';
    testContainer.style.zIndex = '10000';
    testContainer.style.maxWidth = '80%';
    testContainer.style.maxHeight = '80%';
    testContainer.style.overflow = 'auto';
    testContainer.style.display = 'flex';
    testContainer.style.flexDirection = 'column';
    testContainer.style.gap = '10px';
    
    // Title and close button
    testContainer.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <h3 style="margin: 0; font-size: 18px;">Welcome Message Test</h3>
        <button id="close-test" style="background: #eee; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer;">Close</button>
      </div>
      <div style="display: flex; gap: 10px; margin-bottom: 10px;">
        <select id="test-city" style="flex: 1; padding: 8px; border-radius: 4px; border: 1px solid #ccc;">
          ${TOP_CITIES.map(city => `<option value="${city.name}">${city.name}, ${city.country}</option>`).join('')}
        </select>
        <input id="test-name" placeholder="Enter name (optional)" style="flex: 1; padding: 8px; border-radius: 4px; border: 1px solid #ccc;">
        <button id="run-test" style="background: #3b82f6; color: white; border: none; padding: 8px 15px; border-radius: 4px; cursor: pointer;">Generate</button>
      </div>
      <div id="result-container" style="border: 1px solid #eee; padding: 10px; border-radius: 4px; min-height: 100px;">
        <p id="status">Select a city and click Generate to test</p>
        <div id="message-result" style="display: none; margin-top: 10px;"></div>
      </div>
    `;
    
    document.body.appendChild(testContainer);
    
    // Event listeners
    document.getElementById('close-test')?.addEventListener('click', () => {
      document.body.removeChild(testContainer);
    });
    
    document.getElementById('run-test')?.addEventListener('click', async () => {
      const cityName = (document.getElementById('test-city') as HTMLSelectElement).value;
      const userName = (document.getElementById('test-name') as HTMLInputElement).value;
      const statusEl = document.getElementById('status');
      const resultEl = document.getElementById('message-result');
      
      if (statusEl) statusEl.textContent = 'Generating...';
      if (resultEl) resultEl.style.display = 'none';
      
      try {
        // Find the selected city
        const city = TOP_CITIES.find(c => c.name === cityName);
        if (!city) throw new Error('City not found');
        
        // Generate the message
        const message = await generateLLMWelcomeMessage(city, userName || undefined);
        
        // Display result
        if (statusEl) statusEl.textContent = 'Success!';
        if (resultEl) {
          resultEl.innerHTML = `
            <h4 style="margin: 0 0 5px 0; font-size: 14px;">LLM Welcome Message:</h4>
            <div style="background: #f8f9fa; padding: 10px; border-radius: 4px;">${message}</div>
          `;
          resultEl.style.display = 'block';
        }
      } catch (error) {
        console.error('Test error:', error);
        if (statusEl) statusEl.textContent = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      }
    });
    
    return null;
  };

/*   const testOnboarding = () => {
    const userId = localStorage.getItem('tripperUserId') || 'anonymous';
    
    // Force clear any "completed" flag
    localStorage.removeItem(`user_${userId}_onboarded`);
    localStorage.setItem(`user_${userId}_needs_onboarding`, 'true');
    
    // Hide existing messages
    setMessages([]);
    
    // Create empty message first
    const onboardingMessage: Message = {
      id: Date.now().toString(),
      content: '',
      sender: 'bot' as 'bot',
      timestamp: new Date()
    };
    
    // Add empty message and set streaming state
    setMessages([onboardingMessage]);
    setIsStreaming(true);
    setCurrentStreamingMessage(onboardingMessage);
    
    // Get welcome message and stream in content
    userOnboarding.initialize(userId, userData).then(welcomeMessage => {
      let displayedContent = '';
      const streamInterval = setInterval(() => {
        if (displayedContent.length < welcomeMessage.length) {
          // Add a few characters at a time
          const charsToAdd = Math.min(3, welcomeMessage.length - displayedContent.length);
          displayedContent += welcomeMessage.substring(displayedContent.length, displayedContent.length + charsToAdd);
          
          // Update message in UI
          const updatedMessage = {...onboardingMessage, content: displayedContent};
          setMessages([updatedMessage]);
          setCurrentStreamingMessage(updatedMessage);
        } else {
          // Done streaming
          clearInterval(streamInterval);
          setIsStreaming(false);
          setCurrentStreamingMessage(null);
          setOnboardingActive(true);
          
          // Save final message
          addMessageToPersistence({...onboardingMessage, content: welcomeMessage}, true);
        }
      }, 15);
    });
  }; */

  
  const ensureFullContentVisible = () => {
    const container = document.querySelector('.chat-container') || 
                      document.querySelector('.messages-container') ||
                      document.querySelector('.overflow-y-auto');
                      
    if (container && container instanceof HTMLElement) {
      const bufferValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--chat-input-bar-buffer');
      const INPUT_BAR_BUFFER = parseInt(bufferValue) || 160;
      
      setTimeout(() => {
        container.scrollTop = container.scrollHeight + INPUT_BAR_BUFFER;
      }, 100);
      
      setTimeout(() => {
        container.scrollTop = container.scrollHeight + INPUT_BAR_BUFFER;
      }, 300);
      
      setTimeout(() => {
        container.scrollTop = container.scrollHeight + INPUT_BAR_BUFFER;
      }, 800);
    }
  };



  // Add this helper function to the App component
  const addWeatherWidgetToConversation = useCallback((location: string, messageId: string) => {
    // --- console.log(`[ APP - ConversationService] Adding weather widget for ${location}`);
    
    // Create a unique ID for the weather widget
    const widgetId = `weather_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    
    // Create the weather widget data structure
    const weatherWidget = {
      id: widgetId,
      location: location,
      timestamp: new Date().toISOString()
    };

    console.log(`[ APP - ConversationService] Weather widget created:`, weatherWidget);
    // Add to conversation tree
    conversationService.addWeatherWidget(weatherWidget, messageId);
    
    return widgetId;
  }, []);

  function safeAuthCheck(operation: string) {
    try {
      if (import.meta.env.MODE !== 'production') {
        AuthStateChecker.check(operation).catch(err => 
          console.warn('Auth check error:', err)
        );
      }
    } catch (error) {
      console.warn('Error running auth check:', error);
    }
  }

  // Fix 1: Update clearTripperData in App.tsx to preserve user data
  const clearTripperData = async () => {
    // --- console.log('Performing targeted auth state reset... -------------------- -------------------');
    setLoadingMessage('Signing out...');
    setShowLoadingOverlay(true);
    // 1. First, hide chat panel immediately
    //animatePanels('slideOut', 'chat');

    // 2. Clear all map markers
    setLocations([]);
    setSelectedLocation(null);
    resetSeenMarkers(); // This is the utility function that clears marker state

    // 3. Reset map state with correct property name
    mapStateService.setSelectedLocation(null); // Use the proper method
    mapStateService.updateVisibleLocations([]); // Clear visible locations

    try {
      // 1. Try server logout if we have a token
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          // --- console.log('Server logout successful');
        } catch (error) {
          console.error('Server logout error:', error);
        }
      }
      
      // 2. FIXED: Only clear authentication data, not user content
      const authKeys = ['authToken', 'tripperAuthenticated', 'sessionToken', 'tripperLoggedOut'];
      const debugEnabled = localStorage.getItem('ENABLE_AUTH_DEBUG');
      
      // First, backup user data to temporary storage
      const userData = localStorage.getItem('tripper_user_data');
      if (userData) {
        sessionStorage.setItem('tripper_user_data_backup', userData);
        // --- console.log('User data backed up before logout');
      }
      
      // --- console.log('Selectively clearing auth data from localStorage');
      authKeys.forEach(key => localStorage.removeItem(key));
      
      // Clear auth-related session storage
      const sessionKeys = ['socialAuthSuccess', 'socialAuthProvider', 'socialAuthEmail'];
      sessionKeys.forEach(key => sessionStorage.removeItem(key));
      
      // Restore debug setting if needed
      if (debugEnabled) {
        localStorage.setItem('ENABLE_AUTH_DEBUG', debugEnabled);
      }
      
      // 3. Update UI
      setShowUI(false);
      
      // 4. Show confirmation
      alert('You have been logged out successfully');
      
      // 5. Update UI state
      localStorage.setItem('tripperLoggedOut', 'true');

      // Also trigger a custom event that other components can listen for
      window.dispatchEvent(new CustomEvent('tripper-user-logout'));
      
      // Show loading overlay
      //setLoadingMessage('Signing out...');
      //setShowLoadingOverlay(true);
      
      // Use AuthService for logout
      await authService.logout();
      
      // Update UI state after a short delay
      setTimeout(() => {
        //setShowUI(false);
        setShowLoadingOverlay(false);
        setTimeout(() => {
          setShowUI(false); // NOW show auth panel
        }, 300);
      }, 1500);
      
      // --- console.log('Authentication data cleared successfully');
    } catch (error) {
      console.error('Error clearing auth data:', error);
      setShowLoadingOverlay(false);
      setShowUI(false); // Show auth panel on error too
      alert('Error during logout: ' + (error as Error).message);
    }
  };

  // Location comparison helpers
  const areLocationsIdentical = (locationsA: Location[], locationsB: Location[]) => {
    // Quick check by length
    if (locationsA.length !== locationsB.length) {
      return false;
    }
    
    // Map locations by ID for easier comparison
    const locationMapA = new Map(locationsA.map(loc => [loc.id, loc]));
    const locationMapB = new Map(locationsB.map(loc => [loc.id, loc]));
    
    // Check if all IDs match
    if (locationMapA.size !== locationMapB.size) {
      return false;
    }
    
    // Check each location
    for (const [id, locA] of locationMapA.entries()) {
      const locB = locationMapB.get(id);
      
      if (!locB) return false;
      
      // Compare positions
      const posA = locA.position ? `${locA.position.lat.toFixed(6)},${locA.position.lng.toFixed(6)}` : '';
      const posB = locB.position ? `${locB.position.lat.toFixed(6)},${locB.position.lng.toFixed(6)}` : '';
      
      if (posA !== posB) return false;
    }
    
    return true;
  };

  const hasOverlap = (locationsA: Location[], locationsB: Location[]) => {
    return locationsA.some(locA => 
      locationsB.some(locB => 
        locA.name === locB.name && 
        Math.abs((locA.position?.lat || 0) - (locB.position?.lat || 0)) < 0.0001 &&
        Math.abs((locA.position?.lng || 0) - (locB.position?.lng || 0)) < 0.0001
      )
    );
  };

  // ===== CALLBACK HANDLERS =====

  // Add a storage event listener in App.tsx
  window.addEventListener('storage', (e) => {
    if (e.key === 'tripper_user_data') {
      const data = JSON.parse(e.newValue || '{}');
      // --- console.log('[Storage Event] Images count:', data.images?.messages?.length);
    }
  });


  const handleAuthSuccess = useCallback((sessionToken) => {
    // Show loading overlay
    setLoadingMessage('Preparing your experience...hang tight!');
    setShowLoadingOverlay(true);
    
    // Save the token
    localStorage.setItem('sessionToken', sessionToken);
    localStorage.setItem('tripperAuthenticated', 'true');

    if(localStorage.getItem('sessionToken'))
    {
      if(localStorage.getItem('sessionToken')==undefined && localStorage.getItem('tripperUserExists') && localStorage.getItem('tripperHasAccount'))
      {
        localStorage.setItem('tripperLoggedOut', 'true');
      }
    } else {
      if(localStorage.getItem('tripperUserExists') && localStorage.getItem('tripperHasAccount'))
      {
        localStorage.setItem('tripperLoggedOut', 'true');
      }
    }
    //localStorage.setItem('tripperLoggedOut', 'true');
    //localStorage.removeItem('tripperLoggedOut');
    
    // Update UI state after a short delay for smooth transition
    setTimeout(() => {
      setShowUI(true);
      setShowLoadingOverlay(false);
    }, 500);
  }, []);

  const handleLocationsUpdate = useCallback((newLocations: Location[]) => {
    // Update locations in a single state update to avoid blinking
    setLocations(prevLocations => {
      // Skip identical updates
      if (areLocationsIdentical(prevLocations, newLocations)) {
        return prevLocations;
      }
      
      // Reset markers for completely new location sets
      if (!hasOverlap(prevLocations, newLocations)) {
        resetSeenMarkers();
      }
      
      return newLocations;
    });
    
    // Update selected location based on count
    if (newLocations.length > 1) {
      setSelectedLocation(null);
    } else if (newLocations.length === 1) {
      setSelectedLocation(newLocations[0]);
    }
  }, []);

  const handleLocationSelect = useCallback((location: Location | null) => {
    if (location) {
      // Flag that this location was selected from a card
      setLocationSelectedFromCard(true);
      setSelectedLocation(location);
      
      // Reset the flag after a delay
      setTimeout(() => {
        setLocationSelectedFromCard(false);
      }, 2000);
    } else {
      setSelectedLocation(null);
      setLocationSelectedFromCard(false);
    }
  }, []);

  // Add this function to app.tsx
  const extractLocationFromImageResponse = (botMessage: Message): Location | null => {
    // Check if it's an image identification message
    if (!botMessage.content.startsWith('This place is')) {
      return null;
    }
    
    // Extract location name and country
    const pattern = /This place is (.+?) in ([^\.]+)/i;
    const match = botMessage.content.match(pattern);
    
    if (!match) return null;
    
    const locationName = match[1].trim();
    const country = match[2].trim();
    
    // Get description (text after the first period)
    const descriptionMatch = botMessage.content.match(/\.\s*(.+?)$/);
    const description = descriptionMatch ? descriptionMatch[1].trim() : '';
    
    // For city, we can extract it from the country or use a default
    let city = '';
    
    // Extract city based on landmark
    if (locationName === 'Sagrada Familia') {
      city = 'Barcelona';
    } else if (locationName === 'Vernazza') {
      city = 'Vernazza';
    } else if (locationName === 'Eiffel Tower') {
      city = 'Paris';
    } else {
      // Otherwise use first part of country as fallback
      city = country.split(',')[0].trim();
    }
    
    // Real-world coordinates for common landmarks
    let lat = 0, lng = 0;
    if (locationName === 'Sagrada Familia') {
      lat = 41.4036; lng = 2.1744;
    } else if (locationName === 'Vernazza') {
      lat = 44.1359; lng = 9.6839;
    } else if (locationName === 'Eiffel Tower') {
      lat = 48.8584; lng = 2.2945;
    }
    
    return {
      id: `loc-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`,
      name: locationName,
      city: city,
      country: country,
      position: { lat, lng },
      rating: 4.8, // Default high rating for landmarks
      reviews: 15000, // Default value
      description: description
    };
  };
  // In handleImageSearch in App.tsx:
  const handleImageSearch_UNUSED = useCallback(async (images: File[]) => {
    setIsProcessingImages(true);
    try {
      // Process image message as before
      const imageMessages = await Promise.all(images.map(createImageMessage));
      setMessages(prev => [...prev, ...imageMessages]);
      
      imageMessages.forEach(imgMsg => {
        const cleanImageMsg = JSON.parse(JSON.stringify({
          id: imgMsg.id,
          content: imgMsg.content || '',
          sender: imgMsg.sender, 
          timestamp: typeof imgMsg.timestamp === 'object' ? imgMsg.timestamp.toISOString() : imgMsg.timestamp,
          type: 'image',
          imageUrl: imgMsg.imageUrl
        }));
        
        conversationService.addImageMessage(cleanImageMsg);
      });
      
      // Process the location from the image
      const newLocations = await processLocationImages(images);
      
      // Create bot response
      const botMessage: Message = {
        id: Date.now().toString(),
        content: `This place is ${newLocations.map(loc => 
          `${loc.name} in ${loc.country}. ${loc.description}`
        ).join('\n')}`,
        sender: 'bot',
        timestamp: new Date()
      };
      
      // --- console.log('[App] Adding location card to conversation tree: -------------------', newLocations);

      // Add message to UI and conversation tree
      setMessages(prev => [...prev, botMessage]);
      addMessageToPersistence(botMessage, false); // Don't parse here yet
      
      const extractedLocation = newLocations[0]
      conversationService.addLocationCard(
        {
          id: extractedLocation.id,
          name: extractedLocation.name,
          city: extractedLocation.city,
          country: extractedLocation.country,
          position: extractedLocation.position,
          rating: extractedLocation.rating,
          reviews: extractedLocation.reviews,
          description: extractedLocation.description || '',
          timestamp: botMessage.timestamp.toISOString()
        },
        botMessage.id
      );
      
      // Update UI state
      setLocations([extractedLocation]);
      setSelectedLocation(extractedLocation);
      //}
    } catch (error) {
      console.error('[App] Error processing images:', error);
      setError('Failed to process images');
    } finally {
      setIsProcessingImages(false);
    }
  }, []);

  const handleImageSearch = useCallback(async (images: File[]) => {
    setIsProcessingImages(true);
    try {
      // Process image messages as before
      const imageMessages = await Promise.all(images.map(createImageMessage));
      setMessages(prev => [...prev, ...imageMessages]);
      
      imageMessages.forEach(imgMsg => {
        const cleanImageMsg = JSON.parse(JSON.stringify({
          id: imgMsg.id,
          content: imgMsg.content || '',
          sender: imgMsg.sender, 
          timestamp: typeof imgMsg.timestamp === 'object' ? imgMsg.timestamp.toISOString() : imgMsg.timestamp,
          type: 'image',
          imageUrl: imgMsg.imageUrl
        }));
        
        conversationService.addImageMessage(cleanImageMsg);
      });
      
      // Try to process locations from the image
      try {
        const newLocations = await processLocationImages(images);
        
        // If we got locations, handle as before
        if (newLocations && newLocations.length > 0) {
          const botMessage: Message = {
            id: Date.now().toString(),
            content: `This place is ${newLocations.map(loc => 
              `${loc.name} in ${loc.country}. ${loc.description}`
            ).join('\n')}`,
            sender: 'bot',
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, botMessage]);
          addMessageToPersistence(botMessage, false);
          
          const extractedLocation = newLocations[0];
          conversationService.addLocationCard(
            {
              id: extractedLocation.id,
              name: extractedLocation.name,
              city: extractedLocation.city,
              country: extractedLocation.country,
              position: extractedLocation.position,
              rating: extractedLocation.rating,
              reviews: extractedLocation.reviews,
              description: extractedLocation.description || '',
              timestamp: botMessage.timestamp.toISOString()
            },
            botMessage.id
          );
          
          setLocations([extractedLocation]);
          setSelectedLocation(extractedLocation);
        }
      } catch (error: any) {
        // Check if error contains a conversational response
        if (error.message && typeof error.message === 'string' && !error.message.includes('Failed')) {
          // This is a conversational response, not an error
          const botMessage: Message = {
            id: Date.now().toString(),
            content: error.message,
            sender: 'bot',
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, botMessage]);
          addMessageToPersistence(botMessage, true);
        } else {
          // Actual error
          console.error('[App] Error processing images:', error);
          setError('Failed to process images');
        }
      }
    } catch (error) {
      console.error('[App] Error processing images:', error);
      setError('Failed to process images');
    } finally {
      setIsProcessingImages(false);
    }
  }, []);


  // Call this function when login is successful
  const ensureServerDataLoaded = async (userId: string) => {
    // --- console.log('[App] User authenticated, ensuring server data is loaded');
    
    // Set a flag that we're in login process
    localStorage.setItem('login_in_progress', 'true');
    
    try {
      // First initialize the data system with the user ID
      initializeDataSystem(userId);
      
      // Then ALWAYS load data from server first
      const serverDataSuccess = await fullDataSyncHandler.initializeFromServer();
      // --- console.log(`[App] Server data load ${serverDataSuccess ? 'succeeded' : 'failed'}`);
      
      // Mark that server data has been loaded
      localStorage.setItem('server_data_loaded', serverDataSuccess ? 'true' : 'false');
      
      // Notify components that authentication is complete with server data
      window.dispatchEvent(new CustomEvent('tripper-auth-complete', {
        detail: { serverDataLoaded: serverDataSuccess }
      }));
      
      // Remove in-progress flag
      localStorage.removeItem('login_in_progress');
      
      return serverDataSuccess;
    } catch (error) {
      console.error('[App] Error loading server data during login:', error);
      localStorage.removeItem('login_in_progress');
      return false;
    }
  };

  // In App.tsx, add a new component
/*   const MapControlsContainer = () => {

    useEffect(() => {
      if (controlsRef.current) {
        // Use transform instead of left 
        controlsRef.current.style.position = 'fixed';
        controlsRef.current.style.left = '0'; // Reset left to zero
        controlsRef.current.style.transform = 'translateX(-120px)'; // Use transform for initial position
        controlsRef.current.style.transition = 'transform 0.3s ease-out';
      }
    }, []);
    
    return (
      <div 
        ref={controlsRef} 
        className="fixed top-20 z-[9999]"
        style={{ transform: 'translateX(-120px)' }}
      >
        <div className="flex flex-col gap-2">
          <div className="bg-white rounded-lg shadow-md flex flex-col">
            <button 
              onClick={() => window.dispatchEvent(new CustomEvent('map-zoom-in'))}
              className="p-2 hover:bg-gray-100 rounded-t-lg"
            >
              <Plus size={24} />
            </button>
            <div className="h-px bg-gray-200"></div>
            <button 
              onClick={() => window.dispatchEvent(new CustomEvent('map-zoom-out'))}
              className="p-2 hover:bg-gray-100 rounded-b-lg"
            >
              <Minus size={24} />
            </button>
          </div>
          
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('map-reset-view'))}
            className="bg-white rounded-lg shadow-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Reset View
          </button>
        </div>
      </div>
    );
  }; */

  // Update closeItinerary (replace existing function)  
  const closeItinerary = useCallback(() => {
    //animatePanels('slideOut','itinerary');
    ////animatePanels('slideIn', 'chat');
    ////animatePanels('slideIn', 'mapControls', itineraryPanelWidth+16);
    // Use setTimeout for reliable cleanup
    setCurrentItinerary(null);
    setStreamingActivity(false);
/*     setTimeout(() => {
      setCurrentItinerary(null);
      setStreamingActivity(false);
    }, 500); */
  }, []);
  
  const handleItineraryUpdate = useCallback((itinerary: Partial<Itinerary>, isStreaming?: boolean) => {
    console.log('[App] Updating itinerary:', {
      hasDestination: !!itinerary.tripDetails?.destination,
      daysCount: itinerary.days?.length || 0,
      forceResults: !!itinerary._forceShowResults,
      isStreaming
    });
    
    // Update state with new itinerary data
    /* setCurrentItinerary(prev => ({
      ...prev,
      ...itinerary
    })); */
    setCurrentItinerary(prev => {
      const newItinerary = { ...prev, ...itinerary };
      // Force sync update
      setTimeout(() => {
        setShowItinerary(true);
        if (itinerary._forceShowResults) {
          setShowItineraryPanel(true);
        }
      }, 0);
      return newItinerary;
    });
    
    // Update streaming activity state
    setStreamingActivity(isStreaming ?? false);
    
    // Handle different animation behavior based on context
    if (itinerary._forceShowResults) {
      // Coming from chat - slide in the itinerary panel
      // --- console.log('[App] Force showing itinerary panel from chat completion');
      
      // Always show itinerary immediately
      setShowItinerary(true);
      
      // Show the itinerary panel
      ////animatePanels('slideIn', 'itinerary');
      ////animatePanels('slideInWithRef','chat','itinerary', 16);
      ////animatePanels('swapInOut', ['itinerary','chat']);
      //animatePanels('slideInWithRef', ['chat','itinerary'], 0);

      
      // Make sure visibility state is updated
      if (!showItineraryPanel) {
        setShowItineraryPanel(true);
      }
    } else {
      // Standard behavior - swap panels and delay showing content
      // --- console.log('[App] Standard itinerary panel toggle');
      
      // Delay showing content until animation starts
      setTimeout(() => {
        setShowItinerary(true);
        setStreamingActivity(isStreaming ?? false);
      }, 250);
      
      // Swap chat and itinerary panels
      //animatePanels('slideInWithRef', ['chat','itinerary'], 0);
      
      // Make sure visibility state is updated
      if (!showItineraryPanel) {
        setShowItineraryPanel(true);
      }
    }
    
    // Process any location data from itinerary days
    if (itinerary.days?.length) {
      const allLocations = itinerary.days.flatMap(day => 
        day.activities?.map(activity => activity.location) || []
      );
  
      if (allLocations.length > 0) {
        // Set locations array
        setLocations(allLocations);
        
        // Clear selected location for multiple locations
        if (allLocations.length > 1) {
          setSelectedLocation(null);
        }
      }
    } 
    // If no days but we have trip details from chat
    else if (itinerary._forceShowResults && itinerary.tripDetails?.destination) {
      // --- console.log('[App] Have trip details from chat but no days yet');
      
      // You could trigger your itinerary generation here if needed
    }
  }, [showItineraryPanel]);

// Helper function to generate random alphanumeric suffix
function generateRandomSuffix(length = 6) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({length}, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
}


const handleSendMessage = useCallback(async (content: string) => {
  setError(null);
  // Reset help prompts visibility when user sends any message
  setHelpPromptsVisible(false);
  
  const userMessage = createUserMessage(content);
  setMessages(prev => [...prev, userMessage]);

  //addMessageToPersistence(userMessage);

  // Quick check for help - matches the regex in validateQueryAsync
  const isHelpQuery = /^(?:help|help me|need help|i need help|can you help|help please)$/i.test(content.trim());

  // If help is already visible and user tries help again, just return
  if (isHelpQuery && helpPromptsVisible) {
    return; // Don't process duplicate help requests
  }
  
  // Only add to persistence if NOT a help query
  if (!isHelpQuery) {
    addMessageToPersistence(userMessage);
  }

  setIsLoading(true);
  setIsStreaming(true);
  setSelectedLocation(null);

  // Declare bot message at function scope for error handling
  let currentBotMessageId: string | null = null;

  try {
    // CASE 1: Active wizard/specialized flow
    /* if (conversationManager && conversationManager.isInSpecializedFlow()) {
      
      const botMessage = createBotMessage();
      currentBotMessageId = botMessage.id; // Store for error handling
      setMessages(prev => [...prev, botMessage]);
      setCurrentStreamingMessage(botMessage);
      
      const { hasLongGap } = prepareMessagesForClaude(messages);
      if (hasLongGap) {
        cityContext.setCurrentCity('');
      }

      const now = new Date();
      const previousMessages = messages.filter(msg => {
        const hoursAgo = (now.getTime() - new Date(msg.timestamp).getTime()) / (1000 * 60 * 60);
        return hoursAgo <= 24;
      }).slice(-5);
      
      const { response, isSpecializedFlow, isComplete, finalOutput } = 
        await conversationManager.processMessage(content, previousMessages);

      if (isSpecializedFlow) {
        const updatedBotMessage = { ...botMessage, content: response };
        
        setMessages(prev => 
          prev.map(msg => msg.id === botMessage.id ? updatedBotMessage : msg)
        );
        
        console.log('[App] ADDING Specialized flow response:');
        addMessageToPersistence(updatedBotMessage, true);
        
        if (isComplete && finalOutput) {
          try {
            const data = JSON.parse(finalOutput);
            if (data.type === "itineraryPlan") {
              handleItineraryData(data);
              conversationManager.clearActiveWizard();
            }
          } catch (error) {
            console.error('[App] Error processing wizard output:', error);
          }
        }
        
        setIsLoading(false);
        setIsStreaming(false);
        setCurrentStreamingMessage(null);
        return;
      } else {
        // Wizard broke away or returned false
        // We still have a response from the wizard
        const updatedBotMessage = { ...botMessage, content: response };
        
        setMessages(prev => 
          prev.map(msg => msg.id === botMessage.id ? updatedBotMessage : msg)
        );
        
        // IMPORTANT: Persist the message even though wizard broke away
        console.log('[App] ADDING is not in Specialized flow response:');
        addMessageToPersistence(updatedBotMessage, true);
        
        setIsLoading(false);
        setIsStreaming(false);
        setCurrentStreamingMessage(null);
        return;
      }
    } */
    if (conversationManager && conversationManager.isInSpecializedFlow()) {
      const botMessage = createBotMessage();
      currentBotMessageId = botMessage.id;
      setMessages(prev => [...prev, botMessage]);
      setCurrentStreamingMessage(botMessage);
      
      const { hasLongGap } = prepareMessagesForClaude(messages);
      if (hasLongGap) {
        cityContext.setCurrentCity('');
      }

      const now = new Date();
      const previousMessages = messages.filter(msg => {
        const hoursAgo = (now.getTime() - new Date(msg.timestamp).getTime()) / (1000 * 60 * 60);
        return hoursAgo <= 24;
      }).slice(-5);
      
      const { response, isSpecializedFlow, isComplete, finalOutput } = 
        await conversationManager.processMessage(content, previousMessages);

      // Always update and persist - NO IF/ELSE NEEDED
      const updatedBotMessage = { ...botMessage, content: response };
      
      setMessages(prev => 
        prev.map(msg => msg.id === botMessage.id ? updatedBotMessage : msg)
      );
      
      console.log('[App] Adding wizard response to persistence');
      addMessageToPersistence(updatedBotMessage, true);
      
      // Handle completion if needed
      if (isComplete && finalOutput) {
        try {
          const data = JSON.parse(finalOutput);
          if (data.type === "itineraryPlan") {
            handleItineraryData(data);
            conversationManager.clearActiveWizard();
          }
        } catch (error) {
          console.error('[App] Error processing wizard output:', error);
        }
      }
      
      setIsLoading(false);
      setIsStreaming(false);
      setCurrentStreamingMessage(null);
      return;
    }
    // CASE 2: Validate query for special handling
    const validation = await validateQueryAsync(content);

    // CASE 2A: Weather query
    /* if (validation.type === 'weather') {

      console.log('[App] Weather query detected:', validation);
      const weatherLocation = validation.location || cityContext.getCurrentCity();
      
      if (!weatherLocation || weatherLocation.trim() === '') {
        const botMessage = createBotMessage("Which city's weather would you like to check?");
        setMessages(prev => [...prev, botMessage]);
        addMessageToPersistence(botMessage, true);
        
        setIsLoading(false);
        setIsStreaming(false);
        return;
      }
      
      setCurrentWeatherLocation(weatherLocation);
      const botMessage = createBotMessage(`Let me check the current weather in ${weatherLocation}...`);

      // ADD THIS - This is what's missing!
      botMessage._extractedData = {
        weather: { location: weatherLocation }
      };


      console.log('[App] Weather botMessage:', botMessage, " weatherLocation:", weatherLocation);
      setMessages(prev => [...prev, botMessage]);
      addMessageToPersistence(botMessage, true);
      addWeatherWidgetToConversation(weatherLocation, botMessage.id);
      
      setIsLoading(false);
      setIsStreaming(false);
      return;
    } */
    if (validation.type === 'weather') {
      console.log('[App] Weather query detected: ------', validation);
      const weatherLocation = validation.location || cityContext.getCurrentCity();
      
      if (!weatherLocation || weatherLocation.trim() === '') {
        const botMessage = createBotMessage("Which city's weather would you like to check?");
        setMessages(prev => [...prev, botMessage]);
        addMessageToPersistence(botMessage, true);
        
        setIsLoading(false);
        setIsStreaming(false);
        return;
      }
      
      setCurrentWeatherLocation(weatherLocation);
      const botMessage = createBotMessage(`Let me check the current weather in ${weatherLocation}...`);

      // Create weather widget ID
      const weatherWidgetId = `weather_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      
      // Add widget ID to message data
      botMessage._extractedData = {
        weather: { 
          location: weatherLocation,
          widgetId: weatherWidgetId 
        }
      };

      // Create placeholder widget
      const placeholderWidget = {
        id: weatherWidgetId,
        location: weatherLocation,
        temperature: 0,
        condition: 'loading',
        forecast: [],
        timestamp: new Date().toISOString(),
        messageId: botMessage.id
      };

      console.log('[App] Weather botMessage:', botMessage, " weatherLocation:", weatherLocation);
      setMessages(prev => [...prev, botMessage]);
      addMessageToPersistence(botMessage, true);
      
      console.log('[App] Adding placeholder weather widget:', weatherLocation);
      // Add placeholder widget to conversation
      addWeatherWidgetToConversation(weatherLocation, botMessage.id);
      
      // Store widget ID for update callback
      window.pendingWeatherWidgets = window.pendingWeatherWidgets || {};
      
      window.pendingWeatherWidgets[weatherLocation] = {
        widgetId: weatherWidgetId,
        messageId: botMessage.id
      };
      
      setIsLoading(false);
      setIsStreaming(false);
      return;
    }

    // CASE 2B: Help query
    if (validation.type === 'help') {
      // Check if help prompts are already visible
      if (helpPromptsVisible) {
        // Don't show duplicate help message
        setIsLoading(false);
        setIsStreaming(false);
        return;
      }
      
      const helpMessage = createBotMessage('Here are some things you can ask me:', 'help');
      helpMessage.isLastInSequence = false;
      setMessages(prev => [...prev, helpMessage]);
      //addMessageToPersistence(helpMessage, true);
      
      setHelpPromptsVisible(true); // Mark as visible
      
      setIsLoading(false);
      setIsStreaming(false);
      return;
    }
    /* if (validation.type === 'help') {
      const helpMessage = createBotMessage('Here are some things you can ask me:', 'help');
      helpMessage.isLastInSequence = false;
      setMessages(prev => [...prev, helpMessage]);
      addMessageToPersistence(helpMessage, true);
      
      setIsLoading(false);
      setIsStreaming(false);
      return;
    } */

    // CASE 5: Handle flight validation (general queries)
    if (validation.type === 'flight') {
      const botMessage = createBotMessage(`Let me check the flight status for you...`);
      currentBotMessageId = botMessage.id;
      setMessages(prev => [...prev, botMessage]);
      addMessageToPersistence(botMessage, true);
      
      setIsLoading(false);
      setIsStreaming(false);
      
      // Extract and fetch flight data
      setTimeout(async () => {
        try {
          const dataRouter = new DataExtractionRouter();
          const extracted = await dataRouter.analyzeAndExtract(content, 'flight');
          
          if (extracted.length > 0 && extracted[0].data) {
            const flightData = extracted[0].data;
            console.log('[App] Received flight data:', flightData);
            
            // Update message with the data (NO API CALL HERE!)
            const updatedMessage: Message = {
              id: botMessage.id,
              content: `Here's the current status for ${flightData.airline} ${flightData.flightNumber}:`,
              sender: botMessage.sender,
              timestamp: botMessage.timestamp,
              _extractedData: { 
                flight: flightData
              }
            };
            
            setMessages((prev: Message[]) => 
              prev.map(msg => msg.id === botMessage.id ? updatedMessage : msg)
            );
            
            // Update persistence
            conversationService.updateMessage(botMessage.id, {
              _extractedData: { flight: flightData }
            });
            
          } else {
            // No flight data returned
            const errorMessage: Message = {
              id: botMessage.id,
              content: "I couldn't identify a flight number. Please provide a flight number like 'UA123' or 'American Airlines 456'.",
              sender: botMessage.sender,
              timestamp: botMessage.timestamp
            };
            
            setMessages(prev => 
              prev.map(msg => msg.id === botMessage.id ? errorMessage : msg)
            );
          }
        } catch (error) {
          console.error('[App] Error processing flight request:', error);
          const errorMessage: Message = {
            id: botMessage.id,
            content: "I couldn't fetch the flight status. Please check the flight number and try again.",
            sender: botMessage.sender,
            timestamp: botMessage.timestamp
          };
          
          setMessages(prev => 
            prev.map(msg => msg.id === botMessage.id ? errorMessage : msg)
          );
        }
      }, 100);
      
      return;
    }   
    
    const { hasLongGap } = prepareMessagesForClaude(messages);
    if (hasLongGap) {
      cityContext.setCurrentCity('');
    }
    
    // CASE 3: Should start specialized flow
    const shouldUseSpecializedFlow = 
      validation.type === 'itinerary' || 
      validation.type === 'travel' ||
      validation.type === 'restaurant';

    if (conversationManager && shouldUseSpecializedFlow) {
      const { recentMessages } = prepareMessagesForClaude(messages);
      const previousMessages = recentMessages.slice(-5);
      
      const botMessage = createBotMessage();
      currentBotMessageId = botMessage.id; // Store for error handling
      setMessages(prev => [...prev, botMessage]);
      setCurrentStreamingMessage(botMessage);
      
      const { response, isSpecializedFlow, isComplete, finalOutput } = 
        await conversationManager.processMessage(content, previousMessages, validation);
      
      if (!isSpecializedFlow) {
        // Wizard broke away - continue to CASE 4 (standard streaming)
        // Don't return, let it fall through to standard flow
        //setCurrentStreamingMessage(null);
        // Wizard broke away - it already cleared itself
        // User's next message will go through validateQueryAsync
        setIsLoading(false);
        setIsStreaming(false);
        setCurrentStreamingMessage(null);
        return; // Just return, don't process further
      } else if (isSpecializedFlow) {
        const updatedBotMessage = { ...botMessage, content: response };
        
        setMessages(prev => 
          prev.map(msg => msg.id === botMessage.id ? updatedBotMessage : msg)
        );
        
        addMessageToPersistence(updatedBotMessage, true);
        
        if (isComplete && finalOutput) {
          try {
            const data = JSON.parse(finalOutput);
            if (data.type === "itineraryPlan") {
              handleItineraryData(data);
            }
          } catch (error) {
            console.error('[App] Error processing wizard output:', error);
          }
        }
        
        setIsLoading(false);
        setIsStreaming(false);
        setCurrentStreamingMessage(null);
        return;
      }
    }

    // CASE 4: Standard streaming response (general queries)
    // IMPORTANT: Prepare messages BEFORE creating bot message to avoid including it in context
    const { claudeMessages } = prepareMessagesForClaude(messages, content);
    claudeMessages.push({ role: 'user', content });
    
    // NOW create the bot message after preparing context
    const botMessage = createBotMessage();
    currentBotMessageId = botMessage.id; // Store for error handling
    
    // --- console.log('[App] Creating new bot message for streaming:', botMessage.id);
    
    setMessages(prev => [...prev, botMessage]);
    setCurrentStreamingMessage(botMessage);
    
    if (sessionStorage.getItem('wizardJustCompleted') === 'true') {
      claudeMessages.push({
        role: 'assistant',
        content: "I understand this is a new conversation topic."
      });
      claudeMessages.push({
        role: 'user', 
        content: "The previous conversation about reservations or travel plans is now complete. Please treat my next message as a new topic."
      });
      sessionStorage.removeItem('wizardJustCompleted');
    }

    let fullResponse = '';
    const stream = getStreamingChatResponse(claudeMessages);
    
    for await (const chunk of stream) {
      fullResponse += chunk;
      
      // Use captured bot message ID to ensure we update the right message
      setCurrentStreamingMessage(prev => {
        if (prev && prev.id === botMessage.id) {
          return { ...prev, content: fullResponse };
        }
        return prev;
      });
      
      setMessages(prev => {
        const updated = prev.map(msg => 
          msg.id === botMessage.id ? { ...msg, content: fullResponse } : msg
        );
        return updated;
      });
    }

    // --- console.log('[App] Streaming complete, response length:', fullResponse.length);
    // Save the complete message
    addMessageToPersistence({
      ...botMessage,
      content: fullResponse
    }, false);
    
    // Process data extraction after streaming completes
    setMessages(prev => prev.map(msg => 
      msg.id === botMessage.id 
        ? { 
            ...msg, 
            extractionStatus: ExtractionStatus.DETECTING,
            extractionStartTime: new Date()
          } 
        : msg
    ));
    const dataRouter = new DataExtractionRouter();
    // Set up progress callback
    dataRouter.setProgressCallback((status, progress) => {
      setMessages(prev => prev.map(msg => 
        msg.id === botMessage.id 
          ? { 
              ...msg, 
              extractionStatus: status,
              extractionProgress: progress
            } 
          : msg
      ));
    });
    
    const extractedData = await dataRouter.analyzeAndExtract(fullResponse);

    console.log('[ APP - ConversationService] Extracted data:', extractedData);

    if (extractedData.length > 0) {
      const extractedDataObj: any = {};

      extractedData.forEach(extraction => {
        if (extraction.type === 'overview' || extraction.type === 'recommendation') {
          // Don't add yet, handle after loop
        } else if (extraction.type === 'restaurant') {
          console.log('[ APP - ConversationService] Extracting object:', extraction);
          console.log('[ APP - ConversationService] Extracted restaurant data:', extraction.data);
          // Extract just the array from restaurant data
          extractedDataObj.restaurant = Array.isArray(extraction.data) 
            ? extraction.data 
            : extraction.data.restaurant || extraction.data;
          console.log('[ APP - ConversationService] Extracted restaurant object:', extractedDataObj.restaurant);
        } else {
          // Other types (flight, etc.) map directly
          extractedDataObj[extraction.type] = extraction.data;
        }
      });
      
      // Handle location priority: recommendation > overview
      const hasRecommendation = extractedData.some(e => e.type === 'recommendation');
      const hasOverview = extractedData.some(e => e.type === 'overview');

      if (hasRecommendation) {
        const recommendation = extractedData.find(e => e.type === 'recommendation');
        extractedDataObj.locations = recommendation?.data;
      } else if (hasOverview) {
        const overview = extractedData.find(e => e.type === 'overview');
        extractedDataObj.locations = overview?.data;
      }
      
      console.log('[ APP - ConversationService] Extracted data object:', extractedDataObj);
      
      // Update message in conversationService with _extractedData
      conversationService.updateMessage(botMessage.id, { 
        _extractedData: extractedDataObj 
      });
      
      // Update UI state
      const updatedBotMessage = {
        ...botMessage,
        content: fullResponse,
        _extractedData: extractedDataObj,
        extractionStatus: ExtractionStatus.COMPLETE,  // ADD THIS
        extractionProgress: undefined  // ADD THIS
      };
      
      setMessages(prev => prev.map(msg => 
        msg.id === botMessage.id ? updatedBotMessage : msg
      ));
      
      // Now trigger location processing with parseContent=true
      addMessageToPersistence(updatedBotMessage, true);
    } else {
      // No data extracted - mark as complete
      setMessages(prev => prev.map(msg => 
        msg.id === botMessage.id 
          ? { 
              ...msg, 
              extractionStatus: ExtractionStatus.COMPLETE 
            } 
          : msg
      ));
    }

    // Check for embedded itinerary data
    const processedMessage = processStreamingMessage(fullResponse);
    if (processedMessage.itineraryPlan && processedMessage.itineraryPlan.action === 'openPlannerWizard') {
      handleItineraryData({
        type: 'itineraryPlan',
        data: processedMessage.itineraryPlan
      });
    }

  } catch (error) {
    console.error('[App] Error:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    setError(errorMessage);
    
    // If we have a bot message in progress, update it with error
    if (currentBotMessageId) {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === currentBotMessageId 
            ? { ...msg, content: `I'm sorry, I encountered an error: ${errorMessage}` }
            : msg
        )
      );
    } else {
      // Otherwise create a new error message
      const errorBotMessage = createBotMessage(`I'm sorry, I encountered an error: ${errorMessage}`);
      setMessages(prev => [...prev, errorBotMessage]);
    }
  } finally {
    setIsLoading(false);
    setIsStreaming(false);
    setCurrentStreamingMessage(null);
    setTimeout(() => {
      ensureFullContentVisible();
    }, 300);
  }
}, [messages, conversationManager, addMessageToPersistence, addWeatherWidgetToConversation, cityContext, handleItineraryUpdate]);

// Helper function to update message
function updateFlightMessage(flightData: FlightData) {
  const updatedMessage: Message = {
    id: botMessage.id,
    content: `Here's the current status for ${flightData.airline} ${flightData.flightNumber}:`,
    sender: botMessage.sender,
    timestamp: botMessage.timestamp,
    _extractedData: { 
      flight: flightData 
    }
  };
  
  setMessages((prev: Message[]) => 
    prev.map(msg => msg.id === botMessage.id ? updatedMessage : msg)
  );
  
  // Update persistence
  conversationService.updateMessage(botMessage.id, {
    _extractedData: { flight: flightData }
  });
}

// Helper to map extracted status to our FlightData status type
function mapExtractedStatus(status: string): FlightData['status'] {
  const statusMap: Record<string, FlightData['status']> = {
    'on-time': 'scheduled',
    'scheduled': 'scheduled',
    'boarding': 'boarding',
    'departed': 'departed',
    'in-flight': 'in-flight',
    'arrived': 'landed',
    'landed': 'landed',
    'cancelled': 'cancelled',
    'delayed': 'delayed'
  };
  return statusMap[status?.toLowerCase()] || 'scheduled';
}

const addFlightWidgetToConversation = useCallback((flightData: FlightData, messageId: string) => {
  const widgetId = `flight_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  
  const flightWidget = {
    id: widgetId,
    flightData: flightData,
    timestamp: new Date().toISOString()
  };

  conversationService.addFlightWidget(flightWidget, messageId);
  
  return widgetId;
}, []);

// Helper function to handle itinerary data
const handleItineraryData = (data: any) => {
  const destination = Array.isArray(data.data.destination) 
    ? data.data.destination[0] 
    : data.data.destination;
    
  const tripDetails = {
    destination: destination,
    startDate: new Date(data.data.dates.start),
    endDate: new Date(data.data.dates.end),
    travelGroup: data.data.travelGroup,
    preferences: {
      activityTypes: Array.isArray(data.data.interests) 
        ? data.data.interests 
        : (data.data.interests ? [data.data.interests] : [])
    }
  };

  // Update itinerary UI
  handleItineraryUpdate({
    tripDetails: {
      destination: tripDetails.destination,
      startDate: tripDetails.startDate.toISOString(),
      endDate: tripDetails.endDate.toISOString(),
      travelGroup: tripDetails.travelGroup,
    },
    _forceShowResults: true
  });
  
  // Handle locations with unique IDs
  if (data.data.locations && Array.isArray(data.data.locations)) {
    const uniqueLocations = data.data.locations.map(location => ({
      ...location,
      id: `${location.id || `loc-${location.name?.toLowerCase().replace(/\s+/g, '-')}`}-${generateRandomSuffix()}`
    }));
    
    setLocations(uniqueLocations);
  }
  
  // Submit to planner
  setTimeout(() => {
    if (plannerWizardRef.current?.submitForm) {
      const submitted = plannerWizardRef.current.submitForm(tripDetails);
      // --- console.log("[App] Submission result:", submitted);
    }
  }, 500);
};

// Separate streaming handler
/* const handleStreamingResponse = async (content: string, messages: Message[], router: MessageRouter | null) => {
  const botMessage = createBotMessage();
  setMessages(prev => [...prev, botMessage]);
  setCurrentStreamingMessage(botMessage);

  try {
    const { claudeMessages, hasLongGap } = prepareMessagesForClaude(messages, content);
    
    if (hasLongGap) {
      cityContext.setCurrentCity('');
    }

    // Add session reset if needed
    if (sessionStorage.getItem('wizardJustCompleted') === 'true') {
      claudeMessages.push({
        role: 'assistant',
        content: "I understand this is a new conversation topic."
      });
      claudeMessages.push({
        role: 'user', 
        content: "The previous conversation about reservations or travel plans is now complete. Please treat my next message as a new topic."
      });
      sessionStorage.removeItem('wizardJustCompleted');
    }

    let fullResponse = '';
    const stream = getStreamingChatResponse(claudeMessages);
    
    for await (const chunk of stream) {
      fullResponse += chunk;
      
      setCurrentStreamingMessage(prev => prev ? {
        ...prev,
        content: fullResponse
      } : null);
      
      setMessages(prev => 
        prev.map(msg => 
          msg.id === botMessage.id ? { ...msg, content: fullResponse } : msg
        )
      );
    }
    
    // Save to persistence
    addMessageToPersistence({
      ...botMessage,
      content: fullResponse
    }, true);

    // Process data extraction on full response if router available
    if (router) {
      await router.postProcess(
        { 
          type: 'stream', 
          content: fullResponse, 
          complete: true, 
          shouldExtractData: true 
        }, 
        botMessage.id,
        fullResponse
      );
    } else {
      // Fallback: just extract locations using the old method
      processLocationsInBackground(fullResponse, botMessage.id, {
        setLocations,
        onLocationSelect,
        conversationService
      });
    }

    // Check for embedded itinerary data
    const processedMessage = processStreamingMessage(fullResponse);
    if (processedMessage.itineraryPlan && processedMessage.itineraryPlan.action === 'openPlannerWizard') {
      handleItineraryData({
        type: 'itineraryPlan',
        data: processedMessage.itineraryPlan
      });
    }
    
  } catch (error) {
    console.error('[App] Streaming error:', error);
    throw error;
  }
}; */

  // ===== EFFECTS =====
  
  // Create effect to load the welcome message
  

  // Add just this one effect - minimal "debug" that fixes timing
  useEffect(() => {
    // This forces React to "see" the current values
    void currentItinerary;
    void showItineraryPanel;
  }, [currentItinerary, showItineraryPanel]);

  // Add this useEffect to keep track of city context changes
  useEffect(() => {
    // Update the state when mounted
    setCurrentCityContext(cityContext.getCurrentCity());
    
    // Add a listener to cityContext
    const handleCityChange = (city) => {
      // --- console.log(`[CityContext Debug] City context changed to: ${city}`);
      setCurrentCityContext(city);
    };
    
    cityContext.addListener(handleCityChange);
    
    return () => {
      cityContext.removeListener(handleCityChange);
    };
  }, []);

  // Add this effect to listen for content added events
  useEffect(() => {
    const handleContentAdded = () => {
      // --- console.log('[App] Content added event received, scrolling to show new content');
      ensureFullContentVisible();
    };
    
    // Listen for both custom events and standard chat updates
    window.addEventListener('tripper-content-added', handleContentAdded);
    
    return () => {
      window.removeEventListener('tripper-content-added', handleContentAdded);
    };
  }, []);

  // Add a message rotation effect
  useEffect(() => {
    if (!showLoadingOverlay) return;
    
    let messageIndex = 0;
    const rotateInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[messageIndex]);
    }, 3000); // Change message every 3 seconds
    
    return () => clearInterval(rotateInterval);
  }, [showLoadingOverlay]);

  // Then update your message setting logic
  useEffect(() => {
    if (!initialMessage) return;
    const isAuth = localStorage.getItem('tripperAuthenticated') === 'true';
    const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
    
    // Only set the message if no other messages exist
    if (messages.length === 0 && isAuth && !isLoggedOut && initialLocation) {
      setMessages([initialMessage]);
      setLocations([initialLocation]);
      setSelectedLocation(initialLocation);
      
      // Also add to persistence
      addMessageToPersistence(initialMessage, true);
    }
  }, [initialMessage, initialLocation]);

  // Add this effect to trigger the check after rebuild completes
  // Add to your initialization code
  useEffect(() => {
    // Enable scroll debugging in development mode
    // Change false to true to activate debugging
    if (process.env.NODE_ENV !== 'production') {
      debugScroll(false); // Set to true when debugging scroll issues
    }
  }, []);

  // Onboarding effect - fixed version
  /* useEffect(() => {
    const initializeUser = async () => {
      
      // Add this NEW check at the very beginning
      const serverDataLoaded = localStorage.getItem('server_data_loaded');
      if (serverDataLoaded !== 'true') {
        console.log('[Onboarding] Waiting for server data to load first');
        return; // Wait for server data before checking onboarding
      }

      // Check if we've already restored a conversation first
      if (messages.length > 0) {
        // --- console.log('[Onboarding] Conversation already exists, skipping onboarding');
        return; // Skip onboarding if we already have messages
      }
      
      // Check if we're in the middle of restoring a conversation
      if (showRestorationOverlay) {
        // --- console.log('[Onboarding] Conversation restoration in progress, skipping onboarding');
        return; // Skip onboarding during restoration
      }
      
      // Also check if we've already triggered a restore
      const hasRestoredFlag = localStorage.getItem('conversation_restored') === 'true';
      if (hasRestoredFlag && !hasRestoredConversation) {
        // --- console.log('[Onboarding] Waiting for conversation restore to complete');
        return; // Let the restore finish first
      }
      
      // --- console.log('[Onboarding] Checking if user needs onboarding');
      
      // Now check if the user actually needs onboarding
      //const userId = localStorage.getItem('userId') || `user-${Date.now()}`;

     const userId = localStorage.getItem('tripperUserId') || `user-${Date.now()}`;
      if (!localStorage.getItem('tripperUserId')) {
        localStorage.setItem('tripperUserId', userId);
      }
      
      // Check if user has completed onboarding
      const hasCompletedOnboarding = localStorage.getItem(`user_${userId}_onboarded`) === 'true';
      if (hasCompletedOnboarding) {
        // --- console.log('[Onboarding] User has already completed onboarding');
        return; // Skip if already onboarded
      }
      
      // --- console.log('[Onboarding] User needs onboarding, initializing...');
      
      try {
        const welcomeMessage = await userOnboarding.initialize(userId, userData);
        const isActive = userOnboarding.isOnboardingActive();
        
        if (isActive && messages.length === 0) { // Only set if we don't have messages yet
          // --- console.log('[Onboarding] Setting onboarding welcome message');
          setOnboardingActive(true);
          
          // Now we safely add the welcome message
          const onboardingMessage = {
            id: Date.now().toString(),
            content: welcomeMessage,
            sender: 'bot' as 'bot',
            timestamp: new Date()
          };
          
          setMessages([onboardingMessage]);
          addMessageToPersistence(onboardingMessage, true);
        }
      } catch (error) {
        console.error('[Onboarding] Error during initialization:', error);
      }
    };
    
    // Only run after restoration state is set
    if (isAuthenticated && !showLoadingOverlay && !showRestorationOverlay) {
      // Add 500ms delay to ensure restore runs first
      setTimeout(() => {
        initializeUser();
      }, 500);
    }
    //}, [hasRestoredConversation, messages.length, showRestorationOverlay]);
  }, [isAuthenticated, showLoadingOverlay, showRestorationOverlay, messages.length]); */

  useEffect(() => {
    const initializeUser = async () => {
      // Check server data loaded
      const serverDataLoaded = localStorage.getItem('server_data_loaded');
      if (serverDataLoaded !== 'true') {
        console.log('[Onboarding] Waiting for server data to load first');
        return;
      }

      // Check if messages exist
      if (messages.length > 0) {
        return;
      }
      
      // Check if restoring
      if (showRestorationOverlay) {
        return;
      }
      
      // Get REAL userId (no fallback)
      const userId = localStorage.getItem('tripperUserId');
      if (!userId) {
        console.log('[Onboarding] No userId found, skipping onboarding');
        return;
      }
      
      // Check if already onboarded
      const hasCompletedOnboarding = localStorage.getItem(`user_${userId}_onboarded`) === 'true';
      if (hasCompletedOnboarding) {
        return;
      }
      
      // Do onboarding
      /* try {
        //const welcomeMessage = await userOnboarding.initialize(userId, userData);
        //const isActive = userOnboarding.isOnboardingActive();
        
        if (isActive && messages.length === 0) {
          setOnboardingActive(true);
          
          const onboardingMessage = {
            id: Date.now().toString(),
            content: welcomeMessage,
            sender: 'bot' as 'bot',
            timestamp: new Date()
          };
          
          setMessages([onboardingMessage]);
          addMessageToPersistence(onboardingMessage, true);
        }
      } catch (error) {
        console.error('[Onboarding] Error during initialization:', error);
      } */
    };
    
    if (isAuthenticated && !showLoadingOverlay && !showRestorationOverlay) {
      setTimeout(() => {
        initializeUser();
      }, 500);
    }
  }, [isAuthenticated, showLoadingOverlay, showRestorationOverlay, messages.length]);

  // In App.tsx, after the conversationManager useEffect
  useEffect(() => {
    // --- console.log('[App] PlannerWizardRef current value:', plannerWizardRef.current);
  }, [plannerWizardRef.current]);

  /* useEffect(() => {
    debug.log('Testing debug system...');
    // Direct console call to confirm console is working
    
    if (conversationManager) {
      debug.log('Conversation manager exists');
    } else {
      debug.log('Conversation manager is null');
    }
  }, [conversationManager]); */


  /* useEffect(() => {
    debug.log('[App] Initializing conversation manager with AbstractWizard');
    const manager = new ConversationManager();
    setConversationManager(manager);
    debug.log('[App] Conversation manager initialized successfully');
  }, []); */

  // In App.tsx, when component initializes
  useEffect(() => {
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--chat-panel-width')
      .trim();
    
    // Convert "360px" to 360
    const width = parseInt(cssValue, 10);
    //setChatPanelWidth(width);
    //setItineraryPanelWidth(width);
    // --- console.log('[App] Chat panel width: ----------------------- ', width);
  }, []);

  // Add to your App.tsx at the beginning of the App component
  useEffect(() => {
    // ONLY run this check for the Vercel domain
    if (window.location.hostname.includes('vercel.app')) {
      const isAuthenticated = localStorage.getItem('tripperAuthenticated') === 'true';
      const hasAuthToken = !!localStorage.getItem('authToken');
      
      // If authenticated but no token, reset the state
      if (isAuthenticated && !hasAuthToken) {
        // --- console.log('Authentication state reset - token missing but authenticated:true');
        localStorage.setItem('tripperAuthenticated', 'false');
        localStorage.setItem('tripperLoggedOut', 'true');
        //window.location.reload(); // Force reload to apply changes
      }
    }
  }, []);

  // Replace with this improved debug logging
  useEffect(() => {
    // --- console.log(`[App] Messages state updated: ${messages.length} messages`);
    
    // Only log the newest message when it's added
    if (messages.length > 0 && messages.length > previousMessagesLengthRef.current) {
      const newMessage = messages[messages.length - 1];
      // --- console.log(`[App] New message (${newMessage.sender}): "${newMessage.content.substring(0, 50)}..."`);
    }
    
    // Update ref for next comparison
    previousMessagesLengthRef.current = messages.length;
  }, [messages]);

  // Add this ref at the top of your component


  useEffect(() => {
    // Update the auth event handler to properly trigger rebuilds
    const handleAuthEvent = () => {
      const isAuth = localStorage.getItem('tripperAuthenticated') === 'true';
      const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
      
      // --- console.log('[App] Auth event detected - authenticated:', isAuth, 'logged out:', isLoggedOut);
      
      if (isAuth && !isLoggedOut) {
        // Get user ID
        const userId = localStorage.getItem('tripperUserId') || 'anonymous';
        
        // Step 1: Show UI and chat panel with loading state
        setShowUI(true);
        //animatePanels('slideIn', 'chat');
        
        // Step 2: Load server data
        ensureServerDataLoaded(userId).then(success => {
          if (success) {
            // --- console.log('[App] Successfully loaded server data, updating UI');
            
            // Step 3: Trigger conversation rebuild - will update state and fire events
            setHasRestoredConversation(false); // Force rebuild trigger
          } else {
            console.warn('[App] Failed to load server data, still trying to rebuild conversation');
            
            // Try rebuild anyway
            restoreConversationFromDatabase();
          }
        });
      } else if (isLoggedOut) {
        setShowUI(false);
        
        // Hide chat panel when logged out
        //animatePanels('slideOut', 'chat');
      }
    };

    // Listen for auth events
    window.addEventListener('tripperAuthenticated', handleAuthEvent);
    window.addEventListener('tripperLogout', handleAuthEvent);
    
    // Initial check
    handleAuthEvent();
    
    return () => {
      window.removeEventListener('tripperAuthenticated', handleAuthEvent);
      window.removeEventListener('tripperLogout', handleAuthEvent);
    };
  }, []);

  // Initialize Google Maps on app load
  useEffect(() => {
    // --- console.log('[App] Starting Google Maps initialization');
    
    // Initialize Maps and update state
    initGoogleMaps()
      .then(success => {
        // --- console.log('[App] Google Maps initialization result:', success);
        setMapsLoaded(success);
        setMapsError(!success);
      })
      .catch(error => {
        console.error('[App] Error initializing Google Maps:', error);
        setMapsError(true);
      });
  }, []);

  // Add this effect to block initial markers
  useEffect(() => {
    // Start with prevention enabled
    setPreventMarkers(true);
    
    // Clear all markers and location data
    resetSeenMarkers();
    mapStateService.updateMapState({
      visibleLocationIds: []
    });
    
    // Allow markers after a delay
    const timer = setTimeout(() => {
      setPreventMarkers(false);
    }, 2000); // 2 second delay
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Log the initial welcome message to conversation tree
    if (messages.length > 0 && messages[0].sender === 'bot') {
      const welcomeMessage = messages[0];
      
      // Add the welcome message to the conversation tree
      //addMessageToConversationTree(welcomeMessage); // Add this line
      addMessageToPersistence(welcomeMessage, true);
    }
  }, []); // Empty dependency array to run only once on mount

  // Set transparent BG for auth panel
  useEffect(() => {
    // --- console.log("Checking variables ------ ",isAuthenticated, showUI);
  }, []); // Empty array for initialization-only logging

  useEffect(() => {
    // --- console.log('[App] Setting transparent background for auth panel -------------------------- ', isAuthenticated, showUI);
    if(isAuthenticated) 
    { 
      initializeDataSystem();
      // --- console.log('[App] Authenticated, initializing data system'); 
    }
    if (!showUI) {
      // PDS - Initialize the persistent data system
      // Target auth panel elements and make them transparent
      setTimeout(() => {
        const authElements = document.querySelectorAll('[class*="auth"]');
        authElements.forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.backgroundColor = 'transparent';
            el.style.background = 'transparent';
            el.style.boxShadow = 'none';
          }
        });
      }, 100); // Small delay to ensure elements are mounted
    }
  }, [showUI]);

  // Update this effect
  useEffect(() => {
    // Add null check for initialMessage
    if (!initialMessage) return;
    
    // Only set the initial city context if we're using the initial message
    if (messages.length === 1 && messages[0]?.id === initialMessage.id) {
      cityContext.setCurrentCity(initialCity.name);
    }
  }, [initialCity.name, messages, initialMessage]);

  // Update state when authentication changes
  useEffect(() => {
    // --- console.log('[App] Authentication state changed:', isAuthenticated);
    setShowUI(isAuthenticated);
  }, [isAuthenticated]);

 // Client/server sync on initial load
  useEffect(() => {
    syncClientWithServer().then(authenticated => {
      if (authenticated === true) {
        setShowUI(true);
      } else if (authenticated === false) {
        setShowUI(false);
      }
    });
  }, []);

  // Listen for localStorage changes and logout events
  useEffect(() => {
    const updateUIState = () => {
      const isAuth = isAuthenticated;
      const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
      setShowUI(isAuth && !isLoggedOut);
    };

    updateUIState();
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'tripperAuthenticated' || e.key === 'tripperLoggedOut') {
        updateUIState();
      }
    };
    
    const handleLogoutEvent = () => {
      setLoadingMessage('Signing out...');
      setShowLoadingOverlay(true);
      
      setTimeout(() => {
        setShowUI(false);
        setShowLoadingOverlay(false);
      }, 1000);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tripperLogout', handleLogoutEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tripperLogout', handleLogoutEvent);
    };
  }, [isAuthenticated]);

  // Handle OAuth callbacks
  useEffect(() => {
    // And fix the OAuth callback handler which is critical for login detection:
    const handleOAuthCallback = async () => {
      if (window.location.hash.startsWith('#auth-callback')) {
        // --- console.log('[App] OAuth callback detected');
        setShowLoadingOverlay(true);
        setLoadingMessage('...');

        // Add the waitForToken function HERE
        const waitForToken = async () => {
          let attempts = 0;
          while (!localStorage.getItem('authToken') && attempts < 20) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
          }
          if (!localStorage.getItem('authToken')) {
            throw new Error('Token not available after 2 seconds');
          }
        };
        
        const hashParams = new URLSearchParams(window.location.hash.substring(window.location.hash.indexOf('?')));
        const token = hashParams.get('token');
        const authenticated = hashParams.get('authenticated');
        
        // Handle error case
        if (hashParams.get('error')) {
          sessionStorage.setItem('tripperAuthError', hashParams.get('error') || '');
          sessionStorage.setItem('socialAuthError', 'true');
          setShowLoadingOverlay(false);
          window.location.hash = '';
          return;
        }
        
        // Handle successful auth
        /* if (token) {
          // Store token and auth status
          localStorage.setItem('authToken', token);

          if (authenticated === 'true') {
            localStorage.setItem('tripperAuthenticated', 'true');
            localStorage.removeItem('tripperLoggedOut');
          }
          
          // Store other auth data
          if (hashParams.get('email')) localStorage.setItem('tripperUserEmail', hashParams.get('email') || '');
          if (hashParams.get('hasAccount') === 'true') localStorage.setItem('tripperHasAccount', 'true');
          
          // Store session data
          if (hashParams.get('provider')) sessionStorage.setItem('socialAuthProvider', hashParams.get('provider') || '');
          sessionStorage.setItem('socialAuthEmail', hashParams.get('email') || '');
          sessionStorage.setItem('socialAuthSuccess', 'true');
          
          // Add a small delay to ensure token propagates
          await new Promise(resolve => setTimeout(resolve, 100));

          // Force update any global auth headers
          if (window.axios) {
            window.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

          // CRITICAL CHANGE: Load FROM server instead of syncing TO server
          // --- console.log('[App] Loading data from server after authentication');
          await fullDataSyncHandler.initializeFromServer();
          
          // Force authentication state update
          window.dispatchEvent(new Event('tripperAuthenticated'));
          
          window.location.hash = '';
          setTimeout(() => {
            // In OAuth callback or auth event handler:
            if (authenticated === 'true') {
              setShowUI(true);
              setShowRestorationOverlay(true); // Show loading overlay
              
              // Add this critical line to trigger database rebuild
              restoreConversationFromDatabase().then(() => {
                setShowRestorationOverlay(false);
              });
            }
          }, 500);
        } */ 
        /* if (token) {
          // FIRST: Show loading overlay immediately (before auth panel disappears)
          setShowLoadingOverlay(true);
          setLoadingMessage('...');

          // Store token and auth status
          localStorage.setItem('authToken', token);
          
          if (authenticated === 'true') {
            localStorage.setItem('tripperAuthenticated', 'true');
            localStorage.removeItem('tripperLoggedOut');
          }
          
          // Store other auth data
          if (hashParams.get('email')) localStorage.setItem('tripperUserEmail', hashParams.get('email') || '');
          if (hashParams.get('hasAccount') === 'true') localStorage.setItem('tripperHasAccount', 'true');
          
          // Store session data
          if (hashParams.get('provider')) sessionStorage.setItem('socialAuthProvider', hashParams.get('provider') || '');
          sessionStorage.setItem('socialAuthEmail', hashParams.get('email') || '');
          sessionStorage.setItem('socialAuthSuccess', 'true');
          
          // Add a small delay to ensure token propagates to localStorage
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Force a storage event to notify other tabs/components
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'authToken',
            newValue: token,
            url: window.location.href
          }));
          
          // CRITICAL CHANGE: Load FROM server instead of syncing TO server
          // --- console.log('[App] Loading data from server after authentication');
          await fullDataSyncHandler.initializeFromServer();
          
          // Force authentication state update
          window.dispatchEvent(new Event('tripperAuthenticated'));
          
          window.location.hash = '';
          setTimeout(() => {
            // In OAuth callback or auth event handler:
            if (authenticated === 'true') {
              setShowUI(true);
              setShowRestorationOverlay(true); // Show loading overlay
              
              // Add this critical line to trigger database rebuild
              restoreConversationFromDatabase().then(() => {
                setShowRestorationOverlay(false);
              });
            }
          }, 500);
        } */
       /*  if (token) {
          // FIRST: Show loading overlay immediately (before auth panel disappears)
          setShowLoadingOverlay(true);
          setLoadingMessage('...');
          
          // Store token and auth status
          localStorage.setItem('authToken', token);

          // Add userId extraction
          const userId = hashParams.get('userId') || hashParams.get('id');
          if (userId) {
            localStorage.setItem('tripperUserId', userId);
          }
          
          if (authenticated === 'true') {
            localStorage.setItem('tripperAuthenticated', 'true');
            localStorage.removeItem('tripperLoggedOut');
          }
          
          // Store other auth data
          if (hashParams.get('email')) localStorage.setItem('tripperUserEmail', hashParams.get('email') || '');
          if (hashParams.get('hasAccount') === 'true') localStorage.setItem('tripperHasAccount', 'true');
          
          // ADD THIS BLOCK HERE: ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
          let userId = hashParams.get('userId') || hashParams.get('id');
          if (!userId && authenticated === 'true') {
            // Generate a unique userId for new signups
            userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          }
          
          if (userId) {
            localStorage.setItem('tripperUserId', userId);
          }
          // ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑ END OF NEW BLOCK


          // Store session data
          if (hashParams.get('provider')) sessionStorage.setItem('socialAuthProvider', hashParams.get('provider') || '');
          sessionStorage.setItem('socialAuthEmail', hashParams.get('email') || '');
          sessionStorage.setItem('socialAuthSuccess', 'true');
          
          // Add a small delay to ensure token propagates to localStorage
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Force a storage event to notify other tabs/components
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'authToken',
            newValue: token,
            url: window.location.href
          }));
          
          // Load FROM server
          await fullDataSyncHandler.initializeFromServer();
          
          // Force authentication state update (this will hide auth panel)
          window.dispatchEvent(new Event('tripperAuthenticated'));
          
          window.location.hash = '';
          
          // Small delay to ensure smooth transition
          await new Promise(resolve => setTimeout(resolve, 100));
          
          if (authenticated === 'true') {
            setShowUI(true);
            setShowRestorationOverlay(true);
            
            // Trigger database rebuild
            restoreConversationFromDatabase().then(() => {
              setShowRestorationOverlay(false);
              // Loading overlay will be hidden by the rebuild complete handler
            });
          }
          // Now when initializing data system, it will have the proper userId
          const userIdForInit = userId || 'anonymous';
          initializeDataSystem(userIdForInit);
        }
          else {
          setShowLoadingOverlay(false);
        } */

          if (token) {
            // FIRST: Show loading overlay immediately (before auth panel disappears)
            setShowLoadingOverlay(true);
            setLoadingMessage('...');
            
            // Store token and auth status
            localStorage.setItem('authToken', token);

            // Use waitForToken HERE before API calls
            await waitForToken();
            
            // SINGLE userId extraction with fallback generation
            let userId = hashParams.get('userId') || hashParams.get('id');
            if (!userId && authenticated === 'true') {
              // Generate a unique userId for new signups
              userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            
            if (userId) {
              localStorage.setItem('tripperUserId', userId);
            }
            
            if (authenticated === 'true') {
              localStorage.setItem('tripperAuthenticated', 'true');
              localStorage.removeItem('tripperLoggedOut');
            }

             const existingFullName = localStorage.getItem('tripperFullName');
            if (existingFullName) {
              localStorage.setItem('tripperUserName', existingFullName);
            }
            
            // Also check if name came from OAuth
            if (hashParams.get('name')) {
              localStorage.setItem('tripperUserName', hashParams.get('name') || '');
            }
            
            // Store other auth data
            if (hashParams.get('email')) localStorage.setItem('tripperUserEmail', hashParams.get('email') || '');
            if (hashParams.get('hasAccount') === 'true') localStorage.setItem('tripperHasAccount', 'true');
            
            // Store session data
            if (hashParams.get('provider')) sessionStorage.setItem('socialAuthProvider', hashParams.get('provider') || '');
            sessionStorage.setItem('socialAuthEmail', hashParams.get('email') || '');
            sessionStorage.setItem('socialAuthSuccess', 'true');

            if (hashParams.get('name')) {
              localStorage.setItem('tripperUserName', hashParams.get('name') || '');
            }
            
            // Add a small delay to ensure token propagates to localStorage
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Force a storage event to notify other tabs/components
            window.dispatchEvent(new StorageEvent('storage', {
              key: 'authToken',
              newValue: token,
              url: window.location.href
            }));
            
            // Now initialize data system with the proper userId
            const userIdForInit = userId || 'anonymous';
            initializeDataSystem(userIdForInit);
            
            // Load FROM server
            await fullDataSyncHandler.initializeFromServer();
            
            // Force authentication state update (this will hide auth panel)
            window.dispatchEvent(new Event('tripperAuthenticated'));
            
            window.location.hash = '';
            
            // Small delay to ensure smooth transition
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (authenticated === 'true') {
              setShowUI(true);
              setShowRestorationOverlay(true);
              
              // Trigger database rebuild
              restoreConversationFromDatabase().then(() => {
                setShowRestorationOverlay(false);
                // Loading overlay will be hidden by the rebuild complete handler
              });
            }
          }
          else {
            setShowLoadingOverlay(false);
          }
      }
    };
    
    handleOAuthCallback();
    window.addEventListener('hashchange', handleOAuthCallback);
    return () => window.removeEventListener('hashchange', handleOAuthCallback);
  }, []);
  
  // Listen for sync status updates
  /* useEffect(() => {
    const handleSyncStatusUpdate = (event) => {
      // Update sync status state
      setSyncStatus(fullDataSyncHandler.getSyncStatus());
    };
    
    // Add event listener for sync status updates
    window.addEventListener('tripper-sync-status-updated', handleSyncStatusUpdate);
    
    // Initial status
    setSyncStatus(fullDataSyncHandler.getSyncStatus());
    
    // Clean up
    return () => {
      window.removeEventListener('tripper-sync-status-updated', handleSyncStatusUpdate);
    };
  }, []); */

  // In the useEffect for sync status updates:
 /*  useEffect(() => {
    const handleSyncStatusUpdate = (event) => {
      // Transform the SyncState to match your state shape
      const status = fullDataSyncHandler.getSyncStatus();
      setSyncStatus({
        overall: status.status || 'idle',  // Adjust based on actual SyncState properties
        lastSyncTime: status.lastSync || Date.now(),
        details: status.details || {}
      });
    };
    
    // Initial status - same transformation
    const initialStatus = fullDataSyncHandler.getSyncStatus();
    setSyncStatus({
      overall: initialStatus.status || 'idle',
      lastSyncTime: initialStatus.lastSync || Date.now(),
      details: initialStatus.details || {}
    });
    
    window.addEventListener('tripper-sync-status-updated', handleSyncStatusUpdate);
    
    return () => {
      window.removeEventListener('tripper-sync-status-updated', handleSyncStatusUpdate);
    };
  }, []); */


  // Also add this event listener in App.tsx to handle logout events from other sources
  useEffect(() => {
    const handleLogout = () => {
      // --- console.log('[App] Logout event detected, cleaning up UI');
      
      // Hide chat panel
      //animatePanels('slideOut', 'chat');
      
      // Clear map
      setLocations([]);
      setSelectedLocation(null);
      resetSeenMarkers();
      
      // Clear any itinerary
      setCurrentItinerary(null);
      setShowItinerary(false);
    };
    
    window.addEventListener('tripperLogout', handleLogout);
    window.addEventListener('tripper-user-logout', handleLogout);
    
    return () => {
      window.removeEventListener('tripperLogout', handleLogout);
      window.removeEventListener('tripper-user-logout', handleLogout);
    };
  }, []);
  

  // ===== SCROLL MANAGER =====
  // Initialize it only once in a useEffect

  // ===== SCROLL MANAGER ====

/*   React.useEffect(() => {
  // Initialize debug object
  window.DEBUG = {
    trace: [],
    log: function(msg: string, data?: any) {
      const entry = `${new Date().toISOString().substr(14, 9)} ${msg}`;
      this.trace.push({ msg: entry, data });
      console.log(`🔴 ${entry}`, data || '');
    }
  };

  // Intercept the handleItineraryUpdate calls
  const originalUpdate = handleItineraryUpdate;
  // @ts-ignore
  window.__handleItineraryUpdate = (itinerary: any, isStreaming?: boolean) => {
    window.DEBUG.log('handleItineraryUpdate CALLED', { 
      hasDestination: !!itinerary?.tripDetails?.destination,
      forceShow: !!itinerary?._forceShowResults 
    });
    return originalUpdate(itinerary, isStreaming);
  };

  // Log state changes
  window.DEBUG.log('STATE SNAPSHOT', {
    currentItinerary: !!currentItinerary,
    showItinerary,
    showItineraryPanel,
    panelVisible: !!itineraryPanelRef.current,
    panelWidth: itineraryPanelRef.current?.style?.width
  });

}, [handleItineraryUpdate, currentItinerary, showItinerary, showItineraryPanel]);

// Also add this separate effect to monitor state changes
React.useEffect(() => {
  if (window.DEBUG) {
    window.DEBUG.log('ITINERARY STATE CHANGED', {
      hasItinerary: !!currentItinerary,
      destination: currentItinerary?.tripDetails?.destination
    });
  }
}, [currentItinerary]);

React.useEffect(() => {
  if (window.DEBUG) {
    window.DEBUG.log('VISIBILITY STATE CHANGED', {
      showItinerary,
      showItineraryPanel
    });
  }
}, [showItinerary, showItineraryPanel]);
 */


  // ===== RENDER =====
return (
  <SentryErrorBoundary fallback={<div>An error has occurred</div>}>
    <Routes>
      {/* Main app route */}
      <Route path="/" element={
        <div className="relative h-screen flex flex-col">
          {/* Loading overlays - only show when needed */}
          <AnimatePresence>
            {showLoadingOverlay && (
              <LoadingOverlay 
                isVisible={showLoadingOverlay} 
                message={loadingMessage} 
              />
            )}
          </AnimatePresence>
          
          {/* Navbar when authenticated */}
          {showUI && <Navbar />}

          {/* Mobile Desktop Suggestion Banner */}
          {showUI && isMobile && (
            <div 
              className="text-white py-2 px-4 text-center relative"
              style={{ background: 'linear-gradient(135deg, #0395aeff 0%, #2958db 100%)' }}>
               {/* style={{ background: 'var(--primary-gradient)' }} */}
              <div className="flex items-center justify-center gap-2 text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Unlock the full experience on desktop</span>
              </div>
            </div>
          )}

          {/* Main content - flex-1 to take remaining height after navbar */}
          <div className="flex flex-1 overflow-hidden gap-0 p-0 bg-white">
            {/* ALWAYS render the main UI (remove the ternary) */}
            <>
              {/* Chat Panel Container */}
              {/* <div 
                className="flex-shrink-0 relative rounded-lg transition-all duration-300 z-[5000]" 
                style={{ 
                  width: 'var(--chat-panel-width)',
                  minWidth: 'var(--chat-panel-min-width, 300px)',
                  maxWidth: 'var(--chat-panel-max-width, 600px)'
                }} > */}
                <div 
                  className={`flex-shrink-0 relative ${!isMobile ? 'rounded-lg' : ''} transition-all duration-300 z-[5000]`}
                  style={{ 
                    width: isMobile ? '100%' : 'var(--chat-panel-width)',
                    minWidth: isMobile ? '100%' : 'var(--chat-panel-min-width, 300px)',
                    maxWidth: isMobile ? '100%' : 'var(--chat-panel-max-width, 600px)'
                  }}
                >
                {/* Chat Panel */}
                  <div 
                    ref={chatPanelRef} 
                    className="bg-white overflow-y-auto h-full"
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      overflowX: 'visible',
                    }}
                  >
                    {/* Loading overlay inside chat panel */}
                    {showRestorationOverlay && (
                      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10
                              flex flex-col items-center justify-center">
                        <div className="w-12 h-12 border-t-4 border-blue-500 border-solid rounded-full animate-spin"></div>
                        <p className="mt-4 text-lg font-medium text-gray-700">Loading messages...</p>
                      </div>
                    )}
                    <ChatPanel
                      messages={messages}
                      onSendMessage={handleSendMessage}
                      isLoading={isLoading}
                      onLocationsUpdate={handleLocationsUpdate}
                      onLocationSelect={handleLocationSelect}
                      streamingMessage={currentStreamingMessage}
                      selectedLocation={selectedLocation}
                      error={error}
                      weatherLocation={currentWeatherLocation}
                      onImageSearch={handleImageSearch}
                      isProcessingImages={isProcessingImages}
                      onItineraryUpdate={handleItineraryUpdate}
                    />
                  </div>
              </div>
              
              {/* Itinerary Panel */}
              {/* <div 
                ref={itineraryPanelRef}
                className={`relative rounded-lg overflow-hidden bg-white shadow-lg z-[4800] transition-all duration-1500 ${!currentItinerary ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                style={{ 
                  width: currentItinerary ? 'var(--itinerary-panel-width)' : '0px',
                  minWidth: currentItinerary ? 'var(--itinerary-panel-width)' : '0px',
                  flexShrink: 0
                }}
              > */}
              <div 
                ref={itineraryPanelRef}
                className={`relative ${!isMobile ? 'rounded-lg' : ''} overflow-hidden bg-white shadow-lg z-[4800] transition-all duration-1500 ${!currentItinerary ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                style={{ 
                  width: currentItinerary ? (isMobile ? '100vw' : 'var(--itinerary-panel-width)') : '0px',
                  minWidth: currentItinerary ? (isMobile ? '100vw' : 'var(--itinerary-panel-width)') : '0px',
                  flexShrink: isMobile ? 'unset' : 0,
                  position: isMobile && currentItinerary ? 'fixed' : 'relative',
                  top: isMobile && currentItinerary ? 0 : 'auto',
                  left: isMobile && currentItinerary ? 0 : 'auto',
                  height: isMobile && currentItinerary ? '100vh' : 'auto',
                  zIndex: isMobile && currentItinerary ? 9999 : 4800
                }}
              >
               {currentItinerary && (
                    <>
                      <button 
                        onClick={closeItinerary}
                        className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white/90 transition-colors shadow-md"
                        aria-label="Close itinerary"
                      >
                        <X size={20} className="text-gray-600" />
                      </button>
                      <ItineraryPanel
                        key={currentItinerary?.tripDetails?.destination || 'empty'}
                        streamingActivity={streamingActivity}
                        streamingBudget={isStreamingBudget}
                        itinerary={currentItinerary}  
                        onLocationSelect={(locationId) => {
                          const location = locations.find(loc => loc.id === locationId);
                          if (location) handleLocationSelect(location);
                        }}
                        selectedLocationId={selectedLocation?.id}
                        onLocationsUpdate={handleLocationsUpdate}
                        plannerWizardRef={plannerWizardRef} // Correct
                      />
                    </>
                  )}
              </div>
              
              {/* Map Container */}
                {!isMobile && (
                <div className="flex-1 relative rounded-lg overflow-hidden bg-white shadow-lg">
                  <MapWrapper
                    view="osm"
                    locations={locations}
                    onLocationSelect={handleLocationSelect}
                    isLoading={isLoading}
                    isStreaming={isStreaming}
                    selectedLocation={selectedLocation}
                    isProcessingLocation={isProcessingImages}
                    useMapLibre={true}
                  />
                </div>
              )}
            </>
          </div>

        {/* Mobile Navigation - ADD HERE */}
        {/* {isMobile && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t z-[6000] flex shadow-lg">
            <button 
              className={`flex-1 py-3 font-medium transition-colors ${!currentItinerary ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
              onClick={() => {
                setCurrentItinerary(null);
                setShowItinerary(false);
              }}
            >
              <div className="flex flex-col items-center">
                <span className="text-xl mb-1">💬</span>
                <span className="text-xs">Chat</span>
              </div>
            </button>
            <button 
              className={`flex-1 py-3 font-medium transition-colors ${currentItinerary ? 'bg-blue-50 text-blue-600' : 'text-gray-400'}`}
              onClick={() => currentItinerary && setShowItinerary(true)}
              disabled={!currentItinerary}
            >
              <div className="flex flex-col items-center">
                <span className="text-xl mb-1">📅</span>
                <span className="text-xs">Itinerary</span>
              </div>
            </button>
          </div>
        )} */}

          {/* Auth Panel Overlay - OUTSIDE the main container */}
          {!showUI && (
            <div 
              className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-md flex items-center justify-center"
            >
              <div className="relative">
                <AuthPanel
                  isAuthenticated={false}
                  onAuthSuccess={handleAuthSuccess}
                />
              </div>
            </div>
          )}
          {/* Portal container - moved here */}
          <div 
            id="below-panels-portal" 
            style={{ 
              position: 'fixed', 
              inset: 0, 
              zIndex: 90, 
              pointerEvents: 'none' 
            }}
          />
          
          {/* Maps loading indicator */}
          {!mapsLoaded && !mapsError && showUI && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center pointer-events-none">
              <div className="bg-white/90 text-gray-800 px-6 py-4 rounded-lg shadow-xl backdrop-blur-md pointer-events-auto">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-lg font-medium">Loading Google Maps...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      }/>
    </Routes>
  </SentryErrorBoundary>
);
}


